import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import QuizShareCard from '../components/QuizShareCard';
import PersonalProfileCard from '../components/PersonalProfileCard';
import SiteHeader from '../components/SiteHeader';
import QuizShareAspectPicker from '../components/QuizShareAspectPicker';
import QuizShareAvatarPicker from '../components/QuizShareAvatarPicker';
import QuizShareNameField from '../components/QuizShareNameField';
import { QUIZ_URL, SITE_NAME } from '../constants/site';
import { buildFrozenHeartResult, buildPersonalityResult } from '../utils/quizScoring';
import {
  buildShareProfileMeta,
  deriveDefaultShareName,
  getShareCardStats,
  getShareCardSpec,
  readStoredShareAspect,
  readStoredShareAvatarMode,
  readStoredShareDisplayName,
  resolveInitialShareDisplayName,
  resolveShareCardAvatarUrl,
  writeStoredShareAspect,
  writeStoredShareAvatarMode,
  writeStoredShareDisplayName,
  type ShareCardAspect,
  type ShareCardAvatarMode,
} from '../utils/shareCardHelpers';
import { captureShareCard } from '../utils/captureShareCard';
import { preloadQuizQr } from '../utils/quizQr';
import {
  canNativeShareImage,
  canvasToPngBlob,
  isShareCancelled,
  shareOrDownloadPngBlob,
} from '../utils/shareStoryImage';
import { saveQuizShareImage, type QuizShareAction, type QuizShareType } from '../utils/saveQuizShareImage';
import { applyQuizShareImage, mergeExtraDetails, parseExtraDetails } from '../utils/extraDetails';
import { resolveLoginResumeStep } from '../utils/resolveLoginResumeStep';
import { getOptionDisplayLabel } from '../utils/optionText';
import { formatAgeFromDob } from '../utils/calculateAgeFromDob';
import { normalizeContact } from '../utils/normalizeContact';
import { useLanguage } from '../i18n/LanguageContext';

function formatMessage(template: any, vars: Record<string, string> = {}): string {
  if (typeof template !== 'string') return '';
  return Object.entries(vars).reduce(
    (result, [key, value]) => result.replaceAll(`{${key}}`, String(value ?? '')),
    template
  );
}

export default function RegistrationPage({ isDirectQuiz = false }: { isDirectQuiz?: boolean }) {
  const { lang, m, fo, rd } = useLanguage();
  const {
    EXTRA_DETAILS_QUESTIONS,
    NARRATIVE_QUESTIONS,
    PERSONALITY_QUIZ,
    SINGLE_QUIZ,
    FROZEN_HEART_META,
    FROZEN_HEART_TAG_D,
    defaultFormData,
  } = rd;

  const [step, setStep] = useState(() => {
    if (isDirectQuiz) {
      return 7;
    }
    const saved = localStorage.getItem('nagiDatingStep');
    return saved ? parseInt(saved, 10) : 0;
  });

  const [hasReachedSummary, setHasReachedSummary] = useState(() => {
    return localStorage.getItem('nagiDatingReachedSummary') === 'true';
  });

  const [hasReachedFinalSummary, setHasReachedFinalSummary] = useState(() => {
    return localStorage.getItem('nagiDatingReachedFinalSummary') === 'true';
  });

  const [recordId, setRecordId] = useState(() => {
    return localStorage.getItem('nagiDatingRecordId') || '';
  });

  const [isUploading, setIsUploading] = useState<{ idCard: boolean; avatar: boolean }>({ idCard: false, avatar: false });
  const [activeModal, setActiveModal] = useState<'personality' | 'single' | 'bedroom' | null>(null);
  const [personalityQuizIndex, setPersonalityQuizIndex] = useState(0);
  const [personalityAnswers, setPersonalityAnswers] = useState<Record<number, string>>({});
  const [singleQuizIndex, setSingleQuizIndex] = useState(-1);
  const [singleAnswers, setSingleAnswers] = useState<Record<number, string>>({});
  const [singleTriggerTagD, setSingleTriggerTagD] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [showFullSummary, setShowFullSummary] = useState(false);
  const [captionCopied, setCaptionCopied] = useState(false);
  const [shareDisplayName, setShareDisplayName] = useState(() => {
    try {
      const saved = localStorage.getItem('nagiDatingForm');
      if (saved) {
        const parsed = JSON.parse(saved) as { nickname?: unknown; fullName?: unknown };
        const nick = typeof parsed.nickname === 'string' ? parsed.nickname : '';
        const full = typeof parsed.fullName === 'string' ? parsed.fullName : '';
        return resolveInitialShareDisplayName(nick, full);
      }
    } catch {
      // Bỏ qua dữ liệu localStorage không hợp lệ
    }
    return resolveInitialShareDisplayName('', '');
  });
  const [supportsNativeShare, setSupportsNativeShare] = useState(false);
  const [shareCardAspect, setShareCardAspect] = useState<ShareCardAspect>(() => readStoredShareAspect());
  const [shareAvatarMode, setShareAvatarMode] = useState<ShareCardAvatarMode>(() =>
    readStoredShareAvatarMode()
  );

  const [recentCards, setRecentCards] = useState<any[]>([]);

  const [formData, setFormData] = useState<typeof defaultFormData>(() => {
    const saved = localStorage.getItem('nagiDatingForm');
    let base: any = defaultFormData;
    if (saved) {
      try { 
        const parsed = JSON.parse(saved); 
        const ensureArray = (val: unknown): string[] =>
          Array.isArray(val) ? val.map(String) : (typeof val === 'string' && val ? val.split(', ') : []);
        base = { 
          ...defaultFormData, 
          ...parsed,
          fullName: typeof parsed.fullName === 'string' ? parsed.fullName : (defaultFormData.fullName || ''),
          nickname: typeof parsed.nickname === 'string' ? parsed.nickname : (defaultFormData.nickname || ''),
          dob: typeof parsed.dob === 'string' ? parsed.dob : (defaultFormData.dob || ''),
          city: typeof parsed.city === 'string' ? parsed.city : (defaultFormData.city || ''),
          address: typeof parsed.address === 'string' ? parsed.address : (defaultFormData.address || ''),
          job: typeof parsed.job === 'string' ? parsed.job : (defaultFormData.job || ''),
          bodyType: typeof parsed.bodyType === 'string' ? parsed.bodyType : (parsed.body_type || defaultFormData.bodyType || ''),
          currentStatus: typeof parsed.currentStatus === 'string' ? parsed.currentStatus : (parsed.current_status || defaultFormData.currentStatus || ''),
          livingSpace: typeof parsed.livingSpace === 'string' ? parsed.livingSpace : (parsed.living_space || defaultFormData.livingSpace || ''),
          energy: typeof parsed.energy === 'string' ? parsed.energy : (defaultFormData.energy || ''),
          lifestyle: typeof parsed.lifestyle === 'string' ? parsed.lifestyle : (defaultFormData.lifestyle || ''),
          comingOut: typeof parsed.comingOut === 'string' ? parsed.comingOut : (parsed.coming_out || defaultFormData.comingOut || ''),
          futureKids: typeof parsed.futureKids === 'string' ? parsed.futureKids : (parsed.future_kids || defaultFormData.futureKids || ''),
          priority: typeof parsed.priority === 'string' ? parsed.priority : (defaultFormData.priority || ''),
          education: typeof parsed.education === 'string' ? parsed.education : (defaultFormData.education || ''),
          avatar: ensureArray(parsed.avatar),
          role: ensureArray(parsed.role),
          rel: ensureArray(parsed.rel),
          personality: ensureArray(parsed.personality),
          pros: ensureArray(parsed.pros),
          cons: ensureArray(parsed.cons),
          substances: ensureArray(parsed.substances),
          hobbies: ensureArray(parsed.hobbies),
          love: ensureArray(parsed.love),
          futurePlans: ensureArray(parsed.futurePlans),
          financeShared: ensureArray(parsed.financeShared),
          financeMindset: ensureArray(parsed.financeMindset),
          sexView: ensureArray(parsed.sexView),
          age: ensureArray(parsed.age),
          partnerRole: ensureArray(parsed.partnerRole),
          partnerBody: ensureArray(parsed.partnerBody),
          redflag: ensureArray(parsed.redflag),
          extraDetails: parsed.extraDetails || defaultFormData.extraDetails
        };
      } catch {
        // Bỏ qua dữ liệu localStorage không hợp lệ
      }
    }

    // Sanitize quiz results to ensure string fields (prevents .replace crashes from old/bad localStorage data)
    const sanitizeResult = (r: any) => {
      if (!r || typeof r !== 'object') return null;
      return {
        ...r,
        shareTitle: typeof r.shareTitle === 'string' ? r.shareTitle : (r.title || ''),
        shareHook: typeof r.shareHook === 'string' ? r.shareHook : '',
        typeKey: typeof r.typeKey === 'string' ? r.typeKey : 'A',
        variantKey: typeof r.variantKey === 'string' ? r.variantKey : undefined,
        percent: typeof r.percent === 'number' ? r.percent : 50,
      };
    };
    const ed = (base as any).extraDetails || {};
    if (ed.personalityResult) ed.personalityResult = sanitizeResult(ed.personalityResult);
    if (ed.singleResult) ed.singleResult = sanitizeResult(ed.singleResult);
    if (ed.bedroomResult) ed.bedroomResult = sanitizeResult(ed.bedroomResult);

    return base;
  });

  const extraAnswerKey = (id: string | number) => `extra-${id}`;
  const narrativeAnswerKey = (idx: number) => `narrative-${idx}`;

  const getExtraAnswer = (q: { id: string | number; q: string }) => {
    const answers = formData.extraDetails?.additionalAnswers || {};
    const key = extraAnswerKey(q.id);
    return answers[key] ?? answers[q.q] ?? '';
  };

  const getNarrativeAnswer = (idx: number, questionText: string) => {
    const answers = formData.extraDetails?.additionalAnswers || {};
    const key = narrativeAnswerKey(idx);
    return answers[key] ?? answers[questionText] ?? '';
  };

  const isGuestOnQuiz = isDirectQuiz && !recordId && !formData.email;
  const canChooseShareAvatar = Boolean(recordId && formData.email && formData.avatar.length > 0);
  const shareCardAvatarUrl = resolveShareCardAvatarUrl(
    shareAvatarMode,
    formData.avatar,
    canChooseShareAvatar
  );

  useEffect(() => {
    if (!isDirectQuiz) {
      localStorage.setItem('nagiDatingStep', step.toString());
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step, isDirectQuiz]);

  useEffect(() => {
    localStorage.setItem('nagiDatingReachedSummary', hasReachedSummary.toString());
  }, [hasReachedSummary]);

  useEffect(() => {
    localStorage.setItem('nagiDatingReachedFinalSummary', hasReachedFinalSummary.toString());
  }, [hasReachedFinalSummary]);

  useEffect(() => {
    localStorage.setItem('nagiDatingRecordId', recordId);
  }, [recordId]);

  useEffect(() => {
    localStorage.setItem('nagiDatingForm', JSON.stringify(formData));
  }, [formData]);

  const totalSteps = 15; // 0=Login, 1-6=Basic, 7=Summary1, 8-12=Extra, 13=FinalSummary, 14=Success
  const _totalSteps = totalSteps; void _totalSteps; // suppress unused warning
  const progress = step > 0 && step < 7 ? (step / 6) * 100 : (step >= 7 && step < 13 ? 100 : 0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: name === 'email' ? normalizeContact(value) : value
      }));
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, fieldName: 'idCard' | 'avatar') => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const apiKey = import.meta.env.VITE_IMGBB_API_KEY;
    if (!apiKey) {
      alert(m.alerts.apiKeyNotConfigured);
      return;
    }

    setIsUploading(prev => ({ ...prev, [fieldName]: true }));
    try {
      if (fieldName === 'avatar') {
        const uploadedUrls: string[] = [];
        for (const file of files) {
          const uploadData = new FormData();
          uploadData.append('image', file);
          const response = await fetch(`https://api.imgbb.com/1/upload?key=${apiKey}`, { method: 'POST', body: uploadData });
          const result = await response.json();
          if (result.success) {
            uploadedUrls.push(result.data.url);
          }
        }
        if (uploadedUrls.length > 0) {
          setFormData(prev => ({ ...prev, avatar: [...prev.avatar, ...uploadedUrls] }));
        } else {
          alert(m.alerts.imageProcessFailed);
        }
      } else {
        const file = files[0];
        const uploadData = new FormData();
        uploadData.append('image', file);
        const response = await fetch(`https://api.imgbb.com/1/upload?key=${apiKey}`, { method: 'POST', body: uploadData });
        const result = await response.json();
        if (result.success) {
          setFormData(prev => ({ ...prev, idCard: result.data.url }));
        } else {
          alert(m.alerts.imageProcessFailed);
        }
      }
    } catch (error) {
      console.error(error);
      alert(m.alerts.imageProcessNetworkError);
    } finally {
      setIsUploading(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const removeAvatar = (index: number) => {
    setFormData(prev => {
      const newAvatars = [...prev.avatar];
      newAvatars.splice(index, 1);
      return { ...prev, avatar: newAvatars };
    });
  };

  const handleOptionSelect = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDobChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    let numbers = val.replace(/\D/g, '');
    if (numbers.length > 8) numbers = numbers.slice(0, 8);
    
    let formatted = numbers;
    if (numbers.length > 4) {
      formatted = numbers.slice(0,2) + '/' + numbers.slice(2,4) + '/' + numbers.slice(4);
    } else if (numbers.length > 2) {
      formatted = numbers.slice(0,2) + '/' + numbers.slice(2);
    }
    
    setFormData(prev => ({ ...prev, dob: formatted }));
  };

  const toggleMultiSelect = (name: keyof typeof defaultFormData, value: string, max: number = 99) => {
    setFormData(prev => {
      const currentArr = prev[name] as string[];
      if (!Array.isArray(currentArr)) return prev;
      if (currentArr.includes(value)) {
        return { ...prev, [name]: currentArr.filter((item: string) => item !== value) };
      }
      if (currentArr.length < max) {
        return { ...prev, [name]: [...currentArr, value] };
      }
      return prev;
    });
  };

  const renderOptionText = (text: any) => getOptionDisplayLabel(text);

  const prevStep = () => { if (step > 0) setStep(step - 1); };

  const saveToAirtable = async (overrideId?: string, isBackground = false) => {
    if (!isBackground) setIsSubmitting(true);
    try {
      const currentId = overrideId || recordId;
      const payload = {
        ...formData,
        email: normalizeContact(formData.email),
        recordId: currentId || undefined,
      };
      const response = await fetch('/api/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        let errMsg = 'API Error';
        try {
          const errData = await response.json();
          if (errData?.error) errMsg = errData.error;
        } catch {}
        throw new Error(errMsg);
      }
      const data = await response.json();
      
      if (!currentId && data.result?.records?.[0]?.id) {
        setRecordId(data.result.records[0].id);
      }
      return true;
    } catch (e) {
      console.error(e);
      if (!isBackground) {
        const msg = (e as Error).message || m.alerts.genericError;
        alert(msg);
      }
      return false;
    } finally {
      if (!isBackground) setIsSubmitting(false);
    }
  };

  const handleNextStep = async () => {
    if (step === 0) {
      setIsSubmitting(true);
      try {
        const loginRes = await fetch('/api/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: normalizeContact(formData.email),
            pin: formData.pin,
          })
        });
        const loginData = await loginRes.json();
        
        if (loginData.success) {
          const d = loginData.data as Record<string, string | undefined>;
          const splitList = (value?: string) => (value ? value.split(', ').filter(Boolean) : []);

          let formattedDob = d.dob || '';
          if (formattedDob && formattedDob.includes('-') && !formattedDob.includes('/')) {
            const dateObj = new Date(formattedDob);
            if (!isNaN(dateObj.getTime())) {
              const dd = String(dateObj.getDate()).padStart(2, '0');
              const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
              const yyyy = dateObj.getFullYear();
              formattedDob = `${dd}/${mm}/${yyyy}`;
            }
          }

          const serverExtraDetails = parseExtraDetails(d.extra_details);

          setFormData(prev => ({
            ...prev,
            fullName: d.fullName || '',
            nickname: d.nickname || '',
            dob: formattedDob,
            city: d.city || '',
            address: d.address || '',
            height: d.height || '',
            weight: d.weight || '',
            bodyType: d.bodyType || '',
            education: d.education || '',
            income: d.income || '',
            job: d.job || '',
            currentStatus: d.currentStatus || '',
            livingSpace: d.livingSpace || '',
            avatar: splitList(d.avatar),
            orientation: d.orientation || '',
            role: splitList(d.role),
            comingOut: d.comingOut || '',
            rel: splitList(d.rel),
            personality: splitList(d.personality),
            pros: splitList(d.pros),
            cons: splitList(d.cons),
            substances: splitList(d.substances),
            energy: d.energy || '',
            lifestyle: d.lifestyle || '',
            hobbies: splitList(d.hobbies),
            love: splitList(d.love),
            futurePlans: splitList(d.futurePlans),
            futureKids: d.futureKids || '',
            financeShared: splitList(d.financeShared),
            financeMindset: splitList(d.financeMindset),
            sexView: splitList(d.sexView),
            age: splitList(d.age),
            partnerRole: splitList(d.partnerRole),
            partnerBody: splitList(d.partnerBody),
            priority: d.priority || '',
            redflag: splitList(d.redflag),
            messageToAdmin: d.messageToAdmin || '',
            socialLink: d.socialLink || '',
            idCard: d.idCard || '',
            extraDetails: {
              ...prev.extraDetails,
              ...mergeExtraDetails(prev.extraDetails, serverExtraDetails),
            },
          }));
          setRecordId(d.recordId || '');

          const localStep = parseInt(localStorage.getItem('nagiDatingStep') || '0', 10);
          const resume = resolveLoginResumeStep(
            {
              fullName: d.fullName,
              height: d.height,
              orientation: d.orientation,
              energy: d.energy,
              age: d.age,
              avatar: d.avatar,
              socialLink: d.socialLink,
            },
            {
              localStep,
              hasReachedFinalSummary,
              extraDetails: serverExtraDetails,
            }
          );
          setHasReachedSummary(resume.hasReachedSummary);
          setHasReachedFinalSummary(resume.hasReachedFinalSummary);
          setStep(resume.step);

        } else {
          // Khách mới -> Khởi tạo
          const newId = Date.now().toString(36) + Math.random().toString(36).substr(2);
          setRecordId(newId);
          setStep(1);
          // Gửi ngầm không làm đơ UI
          saveToAirtable(newId, true).catch(console.error);
        }
      } catch {
        alert(m.alerts.networkError);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Các bước tiếp theo -> Tự động lưu ngầm, chuyển trang ngay lập tức
      if (step === 6) {
        setHasReachedSummary(true);
        setStep(7);
      } else if (step === 12) {
        setHasReachedFinalSummary(true);
        setStep(13);
      } else if (step === 7) {
        setStep(8);
      } else if (step >= 1 && step < 6) {
        setStep(hasReachedSummary ? 7 : step + 1);
      } else if (step >= 8 && step < 12) {
        setStep(hasReachedFinalSummary ? 13 : step + 1);
      } else {
        setStep(step + 1);
      }

      saveToAirtable(recordId, true).catch(console.error);
    }
  };

  const handleFinalSubmit = async () => {
    if (isGuestOnQuiz || !formData.email) {
      localStorage.setItem('nagiDatingForm', JSON.stringify(formData));
      alert(m.alerts.quizSavedLocally);
      setStep(0);
      return;
    }

    setIsSubmitting(true);
    const success = await saveToAirtable(recordId, false);
    if (success) {
      setStep(14);
      localStorage.removeItem('nagiDatingForm');
      localStorage.removeItem('nagiDatingStep');
      localStorage.removeItem('nagiDatingRecordId');
      localStorage.removeItem('nagiDatingReachedSummary');
      localStorage.removeItem('nagiDatingReachedFinalSummary');
    }
    setIsSubmitting(false);
  };

  const startSingleQuiz = () => {
    setSingleQuizIndex(-1);
    setSingleAnswers({});
    setSingleTriggerTagD(false);
    setActiveModal('single');
  };

  const startPersonalityQuiz = () => {
    setPersonalityQuizIndex(0);
    setPersonalityAnswers({});
    setActiveModal('personality');
  };

  const startBedroomQuiz = () => {
    setActiveModal('bedroom');
  };

  const copyShareCaption = (variant: 'personality' | 'single') => {
    const result =
      variant === 'personality'
        ? formData.extraDetails?.personalityResult
        : formData.extraDetails?.singleResult;
    if (!result) return;

    const quizLabel =
      variant === 'personality' ? m.share.caption.personalityQuizLabel : m.share.caption.singleQuizLabel;
    const singleResult = variant === 'single' ? formData.extraDetails?.singleResult : null;
    const scoreLine = singleResult?.temperature
      ? formatMessage(m.share.caption.scoreFrozenHeart, {
          temperature: singleResult.temperature,
          temperatureLabel: singleResult.temperatureLabel,
        })
      : formatMessage(m.share.caption.scorePersonality, {
          percent: String(result.percent),
          typeKey: result.typeKey,
        });
    const text = formatMessage(m.share.caption.template, {
      quizLabel,
      siteName: SITE_NAME,
      shareTitle: result.shareTitle,
      scoreLine,
      shareHook: result.shareHook,
      quizUrl: QUIZ_URL,
    });

    void navigator.clipboard.writeText(text).then(() => {
      setCaptionCopied(true);
      window.setTimeout(() => setCaptionCopied(false), 2500);
    });
  };

  const goToCreateProfile = () => {
    localStorage.setItem('nagiDatingForm', JSON.stringify(formData));
    setStep(0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleExit = () => {
    if (window.confirm(m.alerts.confirmExit)) {
      localStorage.removeItem('nagiDatingForm');
      localStorage.removeItem('nagiDatingStep');
      localStorage.removeItem('nagiDatingRecordId');
      localStorage.removeItem('nagiDatingReachedSummary');
      localStorage.removeItem('nagiDatingReachedFinalSummary');
      setFormData(defaultFormData);
      setRecordId('');
      setHasReachedSummary(false);
      setHasReachedFinalSummary(false);
      setStep(isDirectQuiz ? 7 : 0);
    }
  };

  const cleanOptionText = (text: any) => getOptionDisplayLabel(text);

  const isSameOption = (current: string, option: string) =>
    getOptionDisplayLabel(current) === getOptionDisplayLabel(option);

  const renderTags = (arr: string[], color: string) => {
    if (!arr || arr.length === 0 || (arr.length === 1 && !arr[0])) return <span style={{ color: '#888' }}>{m.common.emptyValue}</span>;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {arr.map((item: string, i: number) => (
          <span key={i} style={{ background: color, color: '#fff', padding: '4px 10px', borderRadius: '12px', fontSize: '0.85rem' }}>
            {cleanOptionText(item)}
          </span>
        ))}
      </div>
    );
  };

  const handleExtraAnswerChange = (question: string, val: string) => {
    setFormData(prev => ({
      ...prev,
      extraDetails: {
        ...prev.extraDetails,
        additionalAnswers: {
          ...prev.extraDetails.additionalAnswers,
          [question]: val
        }
      }
    }));
  };

  const handlePersonalityAnswer = (answerType: string, event: React.MouseEvent<HTMLButtonElement>) => {
    event.currentTarget.blur();
    const nextAnswers = { ...personalityAnswers, [personalityQuizIndex]: answerType };
    setPersonalityAnswers(nextAnswers);
    if (personalityQuizIndex < PERSONALITY_QUIZ.length - 1) {
      setPersonalityQuizIndex(personalityQuizIndex + 1);
    } else {
      const payload = buildPersonalityResult(nextAnswers, lang);
      setFormData((prev) => ({
        ...prev,
        extraDetails: {
          ...prev.extraDetails,
          personalityResult: payload,
        },
      }));
    }
  };

  // Note: full answering UI for bedroom quiz can be wired in modal. For now, result is shown in landing after manual set or extension.

  const handleSingleAnswer = (
    opt: { type: string; triggerTagD?: boolean },
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.currentTarget.blur();
    const nextAnswers = { ...singleAnswers, [singleQuizIndex]: opt.type };
    const nextTagD = singleTriggerTagD || Boolean(opt.triggerTagD);
    setSingleAnswers(nextAnswers);
    if (opt.triggerTagD) setSingleTriggerTagD(true);
    if (singleQuizIndex < SINGLE_QUIZ.length - 1) {
      setSingleQuizIndex(singleQuizIndex + 1);
    } else {
      const payload = buildFrozenHeartResult(nextAnswers, nextTagD, lang);
      setFormData((prev) => ({
        ...prev,
        extraDetails: {
          ...prev.extraDetails,
          singleResult: payload,
        },
      }));
    }
  };

  const hasActiveQuizResult =
    (activeModal === 'personality' && formData.extraDetails?.personalityResult) ||
    (activeModal === 'single' && formData.extraDetails?.singleResult) ||
    (activeModal === 'bedroom' && (formData.extraDetails as any)?.bedroomResult);

  useEffect(() => {
    if (!activeModal) return;

    const scrollY = window.scrollY;
    const { style } = document.body;
    const prevOverflow = style.overflow;
    const prevPosition = style.position;
    const prevTop = style.top;
    const prevWidth = style.width;

    style.overflow = 'hidden';
    style.position = 'fixed';
    style.top = `-${scrollY}px`;
    style.width = '100%';

    return () => {
      style.overflow = prevOverflow;
      style.position = prevPosition;
      style.top = prevTop;
      style.width = prevWidth;
      window.scrollTo(0, scrollY);
    };
  }, [activeModal]);

  useEffect(() => {
    if (!hasActiveQuizResult) return;
    if (shareDisplayName.trim()) return;

    const stored = readStoredShareDisplayName();
    if (stored) {
      setShareDisplayName(stored);
      return;
    }

    const derived = deriveDefaultShareName(formData.nickname, formData.fullName);
    if (derived) setShareDisplayName(derived);
  }, [
    hasActiveQuizResult,
    formData.nickname,
    formData.fullName,
    shareDisplayName,
  ]);

  const handleShareDisplayNameChange = (value: string) => {
    setShareDisplayName(value);
    writeStoredShareDisplayName(value);
  };

  const handleShareAspectChange = (aspect: ShareCardAspect) => {
    setShareCardAspect(aspect);
    writeStoredShareAspect(aspect);

    // Lazy auto-generate the aspect the user just switched to (if not yet saved)
    // This uses the live visible preview card (with current name + avatar) so the saved image matches exactly what they see.
    // Only for authenticated users who have an active result.
    if (activeModal && recordId && formData.email && !isGuestOnQuiz) {
      const qt: QuizShareType = activeModal;
      const res = qt === 'personality'
        ? formData.extraDetails?.personalityResult
        : formData.extraDetails?.singleResult;

      if (res && !(res as any).shareImages?.[aspect]) {
        // Small delay so the preview has re-rendered with the new aspect
        setTimeout(() => {
          ensureShareImage(qt, aspect, true).catch(console.error);
        }, 80);
      }
    }
  };

  const handleShareAvatarModeChange = (mode: ShareCardAvatarMode) => {
    setShareAvatarMode(mode);
    writeStoredShareAvatarMode(mode);
  };

  const shareCardSpec = getShareCardSpec(shareCardAspect);

  const shareProfileMeta = buildShareProfileMeta(formData.city, formData.dob, lang);

  // Derived values for automatic card generation (used when user never clicks Share/Download)
  const autoDisplayName = shareDisplayName?.trim()
    || deriveDefaultShareName(formData.nickname, formData.fullName)
    || 'Bạn';

  const autoCanUsePersonalAvatar = Boolean(recordId && formData.email && formData.avatar.length > 0);
  const autoAvatarUrl = resolveShareCardAvatarUrl(shareAvatarMode, formData.avatar, autoCanUsePersonalAvatar);

  /**
   * Ensure a share image exists for a specific quizType + aspect.
   * - If the aspect already has an image in the result → skip.
   * - preferVisible=true (when modal is open): capture from the live preview card (current name, avatar, aspect already applied in DOM).
   * - Otherwise: use the hidden auto-capture card (for initial background story generation).
   *
   * This allows:
   *   • Auto only 'story' on quiz completion (cheap, no user action needed)
   *   • Lazy auto the second ratio (feed) only when the user actually switches to it in the modal
   *   • New image when user changes name/avatar and shares (new fingerprint)
   */
  const ensureShareImage = async (
    quizType: QuizShareType,
    aspect: ShareCardAspect,
    preferVisible = false
  ) => {
    if (!recordId || !formData.email || isGuestOnQuiz) return;

    const result = quizType === 'personality'
      ? formData.extraDetails?.personalityResult
      : formData.extraDetails?.singleResult;

    if (!result) return;

    // Already have this exact aspect for this result? Skip.
    if ((result as any).shareImages?.[aspect]) return;

    // Choose which rendered card to capture from
    const visibleId = quizType === 'personality' ? 'personality-result-card' : 'single-result-card';
    const hiddenId = quizType === 'personality' ? 'auto-personality-card' : 'auto-single-card';

    let cardId = hiddenId;
    let cardEl = document.getElementById(hiddenId);

    if (preferVisible) {
      const visibleEl = document.getElementById(visibleId);
      if (visibleEl) {
        cardId = visibleId;
        cardEl = visibleEl;
      }
    }

    if (!cardEl) {
      // Hidden not ready yet (rare) — retry shortly
      setTimeout(() => ensureShareImage(quizType, aspect, preferVisible).catch(console.error), 150);
      return;
    }

    // Use live values if available (modal open), otherwise fall back to auto-derived
    const useDisplayName = (shareDisplayName?.trim() || autoDisplayName);
    const useAvatarMode = shareAvatarMode;
    const canUseAvatarNow = Boolean(recordId && formData.email && formData.avatar.length > 0);

    try {
      setIsGeneratingImage(true);

      const canvas = await captureShareCard(cardEl, cardId, aspect);
      const pngBlob = await canvasToPngBlob(canvas);

      const uploadResult = await saveQuizShareImage({
        blob: pngBlob,
        quizType,
        variantKey: result.variantKey,
        aspectRatio: aspect,
        displayName: useDisplayName,
        shareTitle: result.shareTitle || result.title || '',
        recordId: recordId || undefined,
        shareAction: 'shared',
        avatarMode: canUseAvatarNow ? useAvatarMode : 'default',
      });

      if (uploadResult.saved && uploadResult.imageUrl) {
        setFormData((prev) => {
          const updatedExtraDetails = applyQuizShareImage(
            prev.extraDetails,
            quizType,
            aspect,
            uploadResult.imageUrl!
          );
          return {
            ...prev,
            extraDetails: {
              ...prev.extraDetails,
              additionalAnswers: updatedExtraDetails.additionalAnswers || prev.extraDetails.additionalAnswers,
              personalityResult: updatedExtraDetails.personalityResult ?? prev.extraDetails.personalityResult,
              singleResult: updatedExtraDetails.singleResult ?? prev.extraDetails.singleResult,
            },
          };
        });

        // Persist so the public collection and future logins see the new image
        saveToAirtable(recordId, true).catch(console.error);
      }
    } catch (err) {
      // Non-fatal: user can still manually share
      console.warn('ensureShareImage failed (non-fatal):', err);
    } finally {
      setIsGeneratingImage(false);
    }
  };

  // Note: use ensureShareImage directly for all auto/lazy generation needs.
  // The initial story auto + lazy second-aspect are both handled via ensureShareImage.

  useEffect(() => {
    const hasResult = formData.extraDetails?.personalityResult || formData.extraDetails?.singleResult || (formData.extraDetails as any)?.bedroomResult;
    if (hasResult) preloadQuizQr();
  }, [formData.extraDetails?.personalityResult, formData.extraDetails?.singleResult, (formData.extraDetails as any)?.bedroomResult]);

  useEffect(() => {
    if (!recordId || !formData.email) return;
    const hasQuiz =
      formData.extraDetails?.personalityResult || formData.extraDetails?.singleResult || (formData.extraDetails as any)?.bedroomResult;
    if (!hasQuiz) return;
    saveToAirtable(recordId, true).catch(console.error);

    // Auto-generate default story card in background for authenticated users.
    // This populates the public collection page even if user never opens the share modal.
    // The second ratio (feed) is generated lazily only if/when the user switches to it inside the modal.
    if (!isGuestOnQuiz) {
      if (formData.extraDetails?.personalityResult && !(formData.extraDetails.personalityResult as any).shareImages?.story) {
        requestAnimationFrame(() => {
          ensureShareImage('personality', 'story', false).catch(console.error);
        });
      }
      if (formData.extraDetails?.singleResult && !(formData.extraDetails.singleResult as any).shareImages?.story) {
        requestAnimationFrame(() => {
          ensureShareImage('single', 'story', false).catch(console.error);
        });
      }
      if ((formData.extraDetails as any)?.bedroomResult && !((formData.extraDetails as any).bedroomResult as any).shareImages?.story) {
        requestAnimationFrame(() => {
          ensureShareImage('bedroom', 'story', false).catch(console.error);
        });
      }
    }
  }, [
    recordId,
    formData.email,
    formData.extraDetails?.personalityResult,
    formData.extraDetails?.singleResult,
    isGuestOnQuiz,
  ]);

  useEffect(() => {
    setSupportsNativeShare(canNativeShareImage());
  }, []);

  // Fetch 10 random cards for homepage teaser (includes samples for demo variety)
  useEffect(() => {
    fetch('/api/public/cards?sort=random&limit=10')
      .then((res) => res.json())
      .then((data) => {
        if (data.success && Array.isArray(data.cards)) {
          setRecentCards(data.cards);
        }
      })
      .catch(() => {
        // silent fail for teaser
      });
  }, []);

  const handleShareImage = async (
    cardId: string,
    filename: string,
    quizType: QuizShareType,
    forceDownload = false
  ) => {
    const displayName = shareDisplayName.trim();
    if (!displayName) {
      alert(m.alerts.shareNameRequired);
      return;
    }

    const quizResult =
      quizType === 'personality'
        ? formData.extraDetails?.personalityResult
        : quizType === 'single'
        ? formData.extraDetails?.singleResult
        : (formData.extraDetails as any)?.bedroomResult;

    const cardEl = document.getElementById(cardId);
    if (cardEl) {
      setIsGeneratingImage(true);
      try {
        const canvas = await captureShareCard(cardEl, cardId, shareCardAspect);
        const pngBlob = await canvasToPngBlob(canvas);
        const uploadIntent: QuizShareAction = forceDownload ? 'downloaded' : 'shared';

        const uploadResult = await saveQuizShareImage({
          blob: pngBlob,
          quizType,
          variantKey: quizResult?.variantKey,
          aspectRatio: shareCardAspect,
          displayName,
          shareTitle: quizResult?.shareTitle || quizResult?.title || '',
          recordId: recordId || undefined,
          shareAction: uploadIntent,
          avatarMode: canChooseShareAvatar ? shareAvatarMode : 'default',
        });

        if (uploadResult.saved && uploadResult.imageUrl && recordId && formData.email) {
          setFormData((prev) => {
            const updatedExtraDetails = applyQuizShareImage(
              prev.extraDetails,
              quizType,
              shareCardAspect,
              uploadResult.imageUrl!
            );
            return {
              ...prev,
              extraDetails: {
                ...prev.extraDetails,
                additionalAnswers:
                  updatedExtraDetails.additionalAnswers || prev.extraDetails.additionalAnswers,
                personalityResult:
                  updatedExtraDetails.personalityResult ?? prev.extraDetails.personalityResult,
                singleResult: updatedExtraDetails.singleResult ?? prev.extraDetails.singleResult,
                bedroomResult: updatedExtraDetails.bedroomResult ?? (prev.extraDetails as any).bedroomResult,
              },
            };
          });
        }

        await shareOrDownloadPngBlob(
          pngBlob,
          filename,
          !forceDownload && supportsNativeShare
        );
      } catch (err) {
        if (isShareCancelled(err)) return;
        console.error(err);
        alert(m.alerts.imageGenerateFailed);
      } finally {
        setIsGeneratingImage(false);
      }
    }
  };

  const handleExtraOptionSelect = (
    questionKey: string,
    option: string,
    isCheckbox: boolean,
    maxLimit?: number,
    legacyKey?: string
  ) => {
    const optionText = cleanOptionText(option);
    setFormData((prev) => {
      const answers = prev.extraDetails?.additionalAnswers || {};
      const currentAns = answers[questionKey] ?? (legacyKey ? answers[legacyKey] : '') ?? '';

      if (!isCheckbox) {
        return {
          ...prev,
          extraDetails: {
            ...prev.extraDetails,
            additionalAnswers: { ...answers, [questionKey]: optionText },
          },
        };
      }

      const parseSelected = (): string[] => {
        try {
          return currentAns.startsWith('[')
            ? (JSON.parse(currentAns) as string[])
            : currentAns
              ? [currentAns]
              : [];
        } catch {
          return currentAns ? [currentAns] : [];
        }
      };

      const currentSelected = parseSelected();

      if (!currentSelected.includes(optionText) && maxLimit && currentSelected.length >= maxLimit) {
        alert(formatMessage(m.alerts.maxSelections, { max: String(maxLimit) }));
        return prev;
      }

      const updatedSelected = currentSelected.includes(optionText)
        ? currentSelected.filter((o) => o !== optionText)
        : [...currentSelected, optionText];

      return {
        ...prev,
        extraDetails: {
          ...prev.extraDetails,
          additionalAnswers: { ...answers, [questionKey]: JSON.stringify(updatedSelected) },
        },
      };
    });
  };

  const isExtraOptionSelected = (questionKey: string, option: string, legacyKey?: string) => {
    const answers = formData.extraDetails?.additionalAnswers || {};
    const currentAns = answers[questionKey] ?? (legacyKey ? answers[legacyKey] : '') ?? '';
    if (!currentAns) return false;
    const optionText = cleanOptionText(option);
    if (currentAns.startsWith('[')) {
      try {
        const arr = JSON.parse(currentAns);
        return Array.isArray(arr) && arr.includes(optionText);
      } catch {
        return currentAns === optionText;
      }
    }
    return currentAns === optionText;
  };

  const renderQuizOptionText = (text: any) => getOptionDisplayLabel(text);

  const renderQuizLanding = () => (
    <div className="quiz-grid">
      <article className="quiz-card quiz-card--accent-ice">
        <div className="quiz-card-head">
          <span className="quiz-card-icon" aria-hidden>🧊</span>
          <div>
            <h3 className="quiz-card-title">{m.quiz.frozenHeart.title}</h3>
            <p className="quiz-card-desc">
              {m.quiz.frozenHeart.description}
            </p>
            <p className="quiz-card-meta">{m.quiz.frozenHeart.meta}</p>
          </div>
        </div>
        {formData.extraDetails?.singleResult ? (
          <>
            <p className="quiz-card-result">
              {m.common.resultPrefix} <strong>{formData.extraDetails.singleResult.shareTitle}</strong>
              {formData.extraDetails.singleResult.temperature && (
                <> · {formData.extraDetails.singleResult.temperature}</>
              )}
            </p>
            <button type="button" className="quiz-btn quiz-btn--outline quiz-btn--outline-green" onClick={() => { setSingleQuizIndex(-1); setSingleAnswers({}); setSingleTriggerTagD(false); setFormData(prev => ({ ...prev, extraDetails: { ...prev.extraDetails, singleResult: null } })); setActiveModal('single'); }}>
              Làm lại
            </button>
            <button type="button" className="quiz-btn quiz-btn--primary-green" onClick={() => setActiveModal('single')}>
              {m.common.viewResultAndShare}
            </button>
          </>
        ) : (
          <button type="button" className="quiz-btn quiz-btn--primary-green" onClick={startSingleQuiz}>
            Bắt đầu
          </button>
        )}
      </article>

      <article className="quiz-card quiz-card--accent-gold">
        <div className="quiz-card-head">
          <span className="quiz-card-icon" aria-hidden>🎭</span>
          <div>
            <h3 className="quiz-card-title">{m.quiz.personality.title}</h3>
            <p className="quiz-card-desc">
              {m.quiz.personality.description}
            </p>
            <p className="quiz-card-meta">{m.quiz.personality.meta}</p>
          </div>
        </div>
        {formData.extraDetails?.personalityResult ? (
          <>
            <p className="quiz-card-result">
              {m.common.resultPrefix} <strong>{formData.extraDetails.personalityResult.shareTitle}</strong>
            </p>
            <button type="button" className="quiz-btn quiz-btn--outline quiz-btn--outline-green" onClick={() => { setPersonalityQuizIndex(0); setPersonalityAnswers({}); setFormData(prev => ({ ...prev, extraDetails: { ...prev.extraDetails, personalityResult: null } })); setActiveModal('personality'); }}>
              Làm lại
            </button>
            <button type="button" className="quiz-btn quiz-btn--primary-green" onClick={() => setActiveModal('personality')}>
              {m.common.viewResultAndShare}
            </button>
          </>
        ) : (
          <button type="button" className="quiz-btn quiz-btn--primary-green" onClick={startPersonalityQuiz}>
            Bắt đầu
          </button>
        )}
      </article>

      {/* New 18+ Bedroom Config Quiz */}
      <article className="quiz-card quiz-card--accent-pink" style={{ borderColor: '#ff1493', background: 'rgba(255, 20, 147, 0.06)' }}>
        <div className="quiz-card-head">
          <span className="quiz-card-icon" aria-hidden>🛏️</span>
          <div>
            <h3 className="quiz-card-title">Cấu hình phòng ngủ</h3>
            <p className="quiz-card-desc">
              Khám phá "phần cứng + phần mềm" của bạn trong chuyện ấy. Kết quả 18+ tinh tế, viral.
            </p>
            <p className="quiz-card-meta">6 câu • Kết quả riêng tư • Card cực chất</p>
          </div>
        </div>
        {(formData.extraDetails as any)?.bedroomResult?.shareTitle ? (
          <>
            <p className="quiz-card-result">
              {m.common.resultPrefix} <strong>{(formData.extraDetails as any).bedroomResult.shareTitle}</strong>
            </p>
            <div style={{ margin: '10px 0' }}>
              <QuizShareCard
                id="bedroom-result-card"
                variant="bedroom"
                aspect={shareCardAspect}
                shareTitle={(formData.extraDetails as any).bedroomResult.shareTitle}
                shareHook={(formData.extraDetails as any).bedroomResult.shareHook}
                percent={(formData.extraDetails as any).bedroomResult.percent}
                typeKey={(formData.extraDetails as any).bedroomResult.typeKey}
                variantKey={(formData.extraDetails as any).bedroomResult.variantKey}
                displayName={shareDisplayName.trim() || undefined}
                city={shareProfileMeta.city}
                zodiac={shareProfileMeta.zodiac}
                zodiacSymbol={shareProfileMeta.zodiacSymbol}
                stats={getShareCardStats('bedroom', (formData.extraDetails as any).bedroomResult.typeKey, lang)}
                avatarUrl={shareCardAvatarUrl}
              />
            </div>
            <button type="button" className="quiz-btn quiz-btn--outline quiz-btn--outline-green" onClick={() => { setFormData(prev => ({ ...prev, extraDetails: { ...prev.extraDetails, bedroomResult: null } })); setActiveModal('bedroom'); }}>
              Làm lại
            </button>
            <button type="button" className="quiz-btn quiz-btn--primary-green" onClick={() => handleShareImage('bedroom-result-card', `cau-hinh-phong-ngu-${shareCardSpec.filenameSuffix}`, 'bedroom')}>
              Chia sẻ Card (18+)
            </button>
          </>
        ) : (
          <button type="button" className="quiz-btn quiz-btn--primary-green" onClick={startBedroomQuiz}>
            Bắt đầu (18+)
          </button>
        )}
      </article>
    </div>
  );

  const renderQuizCollectionLink = () => {
    if (!recordId) return null;
    const hasAnyResult = formData.extraDetails?.personalityResult || formData.extraDetails?.singleResult;
    if (!hasAnyResult) return null;

    const publicUrl = `/cards/${recordId}`;
    return (
      <div className="quiz-collection-link">
        <a href={publicUrl} target="_blank" rel="noreferrer" className="quiz-collection-link-btn">
          📸 Xem tất cả thẻ card đã tạo (trang công khai)
        </a>
        <div className="quiz-collection-hint">Ai cũng có thể xem trang này nếu có liên kết</div>
      </div>
    );
  };

  const hasSummaryText = (value?: unknown) => {
    if (typeof value !== 'string') return false;
    return Boolean(value.trim());
  };

  const hasSummaryTags = (arr?: unknown[]) =>
    Boolean(Array.isArray(arr) && arr.length && !(arr.length === 1 && typeof arr[0] === 'string' && !arr[0].trim()));

  const renderSummarySectionTitle = (full: string, short: string) => (
    <h4 className="profile-summary-section-title">
      <span className="profile-summary-section-title--desktop">{full}</span>
      <span className="profile-summary-section-title--mobile">{short}</span>
    </h4>
  );

  const renderSummaryLine = (label: string, value: React.ReactNode, filled: boolean) => (
    <p className={`profile-summary-line${filled ? ' profile-summary-line--filled' : ' profile-summary-line--empty'}`}>
      <span className="profile-summary-line__label">{label}</span>
      <span className="profile-summary-line__value">{filled ? value : m.common.emptyValue}</span>
    </p>
  );

  const renderSummaryTagBlock = (label: string, tags: string[], color: string) => {
    const filled = hasSummaryTags(tags);
    return (
      <div
        className={`profile-summary-tag-block${
          filled ? ' profile-summary-tag-block--filled' : ' profile-summary-tag-block--empty'
        }`}
      >
        <p className="profile-summary-tag-block__label">{label}</p>
        <div className="profile-summary-tag-block__value">{renderTags(tags, color)}</div>
      </div>
    );
  };

  const renderSummaryEditButton = (targetStep: number) => (
    <button
      type="button"
      className="profile-summary-edit"
      onClick={() => setStep(targetStep)}
      disabled={isSubmitting}
    >
      {m.common.edit}
    </button>
  );

  const hasSupplementAnswers = Object.keys(formData.extraDetails?.additionalAnswers || {}).length > 0;

  const formatExtraAnswerDisplay = (ans: any) => {
    if (Array.isArray(ans)) return ans.join(', ');
    if (typeof ans !== 'string') return String(ans || '');
    return ans.replace(/[[\]"]/g, '').replace(/,/g, ', ');
  };

  const renderProfileGallery = () => {
    if (!formData.avatar.length && !formData.idCard) return null;
    return (
      <div className="profile-summary-gallery">
        {formData.avatar.map((url, i) => (
          <img key={i} src={url} alt={`avatar-${i}`} className="profile-summary-gallery-img" />
        ))}
        {formData.idCard && (
          <div className="profile-summary-gallery-id">
            <div className="profile-summary-gallery-id-badge">{m.summary.idCardBadge}</div>
            <img src={formData.idCard} alt="CCCD" className="profile-summary-gallery-img" />
          </div>
        )}
      </div>
    );
  };

  const renderProfileSummaryCardItems = () => {
    const fullNameDisplay = [formData.fullName, formData.nickname ? `(${formData.nickname})` : '']
      .filter(Boolean)
      .join(' ')
      .trim();
    const dobDisplay = formData.dob
      ? `${formData.dob} (${formatAgeFromDob(formData.dob)})`
      : '';
    const addressDisplay = [formData.address, formData.city].filter(Boolean).join(', ');
    const heightWeightDisplay = formatMessage(m.summary.heightWeightValue, {
      height: formData.height,
      weightPart: formData.weight
        ? formatMessage(m.summary.weightPart, { weight: formData.weight })
        : '',
    });
    const statusDisplay = [
      formData.currentStatus ? cleanOptionText(formData.currentStatus) : '',
      formData.livingSpace ? cleanOptionText(formData.livingSpace) : '',
    ]
      .filter(Boolean)
      .join(' — ');
    const orientationDisplay = [
      formData.orientation ? cleanOptionText(formData.orientation) : '',
      formData.role.length ? formData.role.map(cleanOptionText).join(', ') : '',
    ]
      .filter(Boolean)
      .join(' / ');
    const energyLifestyleDisplay = [
      formData.energy ? cleanOptionText(formData.energy) : '',
      formData.lifestyle ? cleanOptionText(formData.lifestyle) : '',
    ]
      .filter(Boolean)
      .join(' / ');

    return (
      <>
        <div className="profile-summary-card">
          {renderSummaryEditButton(1)}
          {renderSummarySectionTitle(m.summary.section1Title, m.summary.section1TitleShort)}
          {renderSummaryLine(m.summary.fullName, fullNameDisplay, hasSummaryText(formData.fullName))}
          {renderSummaryLine(m.summary.dob, dobDisplay, hasSummaryText(formData.dob))}
          {renderSummaryLine(m.summary.contact, formData.email, hasSummaryText(formData.email))}
          {renderSummaryLine(m.summary.address, addressDisplay, hasSummaryText(addressDisplay))}
        </div>

        <div className="profile-summary-card">
          {renderSummaryEditButton(2)}
          {renderSummarySectionTitle(m.summary.section2Title, m.summary.section2TitleShort)}
          {renderSummaryLine(m.summary.heightWeight, heightWeightDisplay, hasSummaryText(formData.height))}
          {renderSummaryLine(
            m.summary.bodyType,
            formData.bodyType ? cleanOptionText(formData.bodyType) : '',
            hasSummaryText(formData.bodyType)
          )}
          {renderSummaryLine(m.summary.education, formData.education, hasSummaryText(formData.education))}
          {renderSummaryLine(m.summary.job, formData.job, hasSummaryText(formData.job))}
          {renderSummaryLine(m.summary.income, formData.income, hasSummaryText(formData.income))}
          {renderSummaryLine(m.summary.status, statusDisplay, Boolean(statusDisplay))}
        </div>

        <div className="profile-summary-card profile-summary-card--wide">
          {renderSummaryEditButton(3)}
          {renderSummarySectionTitle(m.summary.section3Title, m.summary.section3TitleShort)}
          {renderSummaryLine(m.summary.orientationRole, orientationDisplay, Boolean(orientationDisplay))}
          {renderSummaryLine(
            m.summary.comeOut,
            formData.comingOut ? cleanOptionText(formData.comingOut) : '',
            hasSummaryText(formData.comingOut)
          )}
          {renderSummaryTagBlock(`${m.steps.step3.relationship}:`, formData.rel, '#8b5cf6')}
          {renderSummaryTagBlock(`${m.steps.step3.personality}:`, formData.personality, '#3b82f6')}
          <div className="profile-summary-tag-columns">
            {renderSummaryTagBlock(m.summary.pros, formData.pros, '#10b981')}
            {renderSummaryTagBlock(m.summary.cons, formData.cons, '#f59e0b')}
          </div>
          {renderSummaryTagBlock(m.summary.substances, formData.substances, '#6b7280')}
        </div>

        <div className="profile-summary-card profile-summary-card--wide">
          {renderSummaryEditButton(4)}
          {renderSummarySectionTitle(m.summary.section4Title, m.summary.section4TitleShort)}
          {renderSummaryLine(
            `${m.steps.step4.energy} & ${m.steps.step4.lifestyle}:`,
            energyLifestyleDisplay,
            Boolean(energyLifestyleDisplay)
          )}
          {renderSummaryLine(
            m.summary.kidsView,
            formData.futureKids ? cleanOptionText(formData.futureKids) : '',
            hasSummaryText(formData.futureKids)
          )}
          {renderSummaryTagBlock(m.summary.hobbies, formData.hobbies, '#3b82f6')}
          {renderSummaryTagBlock(`${m.steps.step4.loveLanguage}:`, formData.love, '#ec4899')}
          {renderSummaryTagBlock(`${m.steps.step4.futurePlans}:`, formData.futurePlans, '#8b5cf6')}
          <div
            className={`profile-summary-tag-block${
              hasSummaryTags(formData.financeShared) || hasSummaryTags(formData.financeMindset)
                ? ' profile-summary-tag-block--filled'
                : ' profile-summary-tag-block--empty'
            }`}
          >
            <p className="profile-summary-tag-block__label">{m.summary.finance}</p>
            <div className="profile-summary-tag-block__value">
              {renderTags(formData.financeShared, '#10b981')}
              <div style={{ marginTop: '5px' }}>{renderTags(formData.financeMindset, '#10b981')}</div>
            </div>
          </div>
          {renderSummaryTagBlock(m.summary.sexView, formData.sexView, '#f59e0b')}
        </div>

        <div className="profile-summary-card profile-summary-card--wide">
          {renderSummaryEditButton(5)}
          {renderSummarySectionTitle(m.summary.section5Title, m.summary.section5TitleShort)}
          {renderSummaryLine(
            `${m.steps.step5.priority}:`,
            formData.priority ? cleanOptionText(formData.priority) : '',
            hasSummaryText(formData.priority)
          )}
          <div
            className={`profile-summary-tag-block${
              hasSummaryTags(formData.age) ||
              hasSummaryTags(formData.partnerRole) ||
              hasSummaryTags(formData.partnerBody)
                ? ' profile-summary-tag-block--filled'
                : ' profile-summary-tag-block--empty'
            }`}
          >
            <p className="profile-summary-tag-block__label">{m.summary.partnerPrefs}</p>
            <div className="profile-summary-tag-block__value">
              {renderTags(formData.age, '#ec4899')}
              <div style={{ marginTop: '5px' }}>{renderTags(formData.partnerRole, '#ec4899')}</div>
              <div style={{ marginTop: '5px' }}>{renderTags(formData.partnerBody, '#ec4899')}</div>
            </div>
          </div>
          {renderSummaryTagBlock(m.summary.redflag, formData.redflag, 'var(--error-color)')}
          <div
            className={`profile-summary-message${
              formData.messageToAdmin ? ' profile-summary-message--filled' : ' profile-summary-message--empty'
            }`}
          >
              <p className="profile-summary-message__label">{m.summary.message}</p>
              {formData.messageToAdmin ? (
                <p className="profile-summary-message__value">
                  <i>"{formData.messageToAdmin}"</i>
                </p>
              ) : (
                <p className="profile-summary-message__value">{m.common.emptyValue}</p>
              )}
          </div>
        </div>

        <div className="profile-summary-card profile-summary-card--wide">
          {renderSummaryEditButton(6)}
          {renderSummarySectionTitle(m.summary.section6Title, m.summary.section6TitleShort)}
          <p
            className={`profile-summary-social profile-summary-line${
              formData.socialLink ? ' profile-summary-line--filled' : ' profile-summary-line--empty'
            }`}
          >
            <span className="profile-summary-line__label">{m.summary.social}</span>
            <span className="profile-summary-line__value">
              {formData.socialLink ? (
                <a href={formData.socialLink} target="_blank" rel="noreferrer">
                  {formData.socialLink}
                </a>
              ) : (
                m.common.emptyValue
              )}
            </span>
          </p>
          {formData.idCard && (
            <p className="profile-summary-id-note">{m.summary.idCardSecure}</p>
          )}
        </div>
      </>
    );
  };

  const renderProfileSummaryCards = () => (
    <div className="profile-summary-grid">{renderProfileSummaryCardItems()}</div>
  );

  const renderExtraQuestionsCard = (
    title: string,
    titleShort: string,
    targetStep: number,
    questions: typeof EXTRA_DETAILS_QUESTIONS
  ) => (
    <div className="profile-summary-card profile-summary-card--wide">
      {renderSummaryEditButton(targetStep)}
      {renderSummarySectionTitle(title, titleShort)}
      {questions.map((q) => {
        const ans = getExtraAnswer(q);
        return (
          <div key={q.id} className="profile-summary-qa profile-summary-qa--answer-only">
            <p className="profile-summary-qa-q">{q.q}</p>
            <p className={`profile-summary-qa-a${ans ? '' : ' profile-summary-qa-a--empty'}`}>
              {ans ? formatExtraAnswerDisplay(ans) : m.common.notAnswered}
            </p>
          </div>
        );
      })}
    </div>
  );

  const renderStep7FullSummary = () => (
    <div className="profile-summary">
      <h2 className="profile-summary-page-title">{m.summary.title}</h2>
      <p className="profile-summary-hint">
        {m.summary.hint} <strong>{m.summary.hintEdit}</strong> {m.summary.hintSuffix}
      </p>
      {renderProfileGallery()}
      {renderProfileSummaryCards()}
      <div className="profile-summary-quiz-section">
        {renderQuizLanding()}
        {renderQuizCollectionLink()}
      </div>
      <div className="profile-summary-actions">
        <button
          type="button"
          className="profile-summary-btn profile-summary-btn--outline"
          onClick={() => setStep(8)}
          disabled={isSubmitting}
        >
          {hasSupplementAnswers ? m.common.supplementDone : m.common.supplementDetails}
        </button>
        <button
          type="button"
          className="primary-btn profile-summary-btn profile-summary-btn--submit"
          onClick={handleFinalSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? m.common.submittingData : m.common.confirmSubmit}
        </button>
      </div>
    </div>
  );

  const renderStep13FullSummary = () => (
    <div className="profile-summary">
      <h2 className="profile-summary-page-title">{m.summaryFull.title}</h2>
      <p className="profile-summary-hint">
        {m.summaryFull.hint} <strong>{m.summaryFull.hintEdit}</strong> {m.summaryFull.hintSuffix}
      </p>
      {renderProfileGallery()}
      <div className="profile-summary-grid">
        {renderProfileSummaryCardItems()}
        {renderExtraQuestionsCard(
          m.summaryFull.psychTitle,
          m.summaryFull.psychTitleShort,
          8,
          EXTRA_DETAILS_QUESTIONS.filter((q) => q.sectionKey === 'psych')
        )}
        {renderExtraQuestionsCard(
          m.summaryFull.practicalTitle,
          m.summaryFull.practicalTitleShort,
          9,
          EXTRA_DETAILS_QUESTIONS.filter((q) => q.sectionKey === 'practical')
        )}
        {renderExtraQuestionsCard(
          m.summaryFull.sexTitle,
          m.summaryFull.sexTitleShort,
          10,
          EXTRA_DETAILS_QUESTIONS.filter((q) => q.sectionKey === 'sex')
        )}
        {renderExtraQuestionsCard(
          m.summaryFull.pressureTitle,
          m.summaryFull.pressureTitleShort,
          11,
          EXTRA_DETAILS_QUESTIONS.filter((q) => q.sectionKey === 'pressure')
        )}
        <div className="profile-summary-card profile-summary-card--wide">
          {renderSummaryEditButton(12)}
          {renderSummarySectionTitle(m.summaryFull.narrativeTitle, m.summaryFull.narrativeTitleShort)}
          {NARRATIVE_QUESTIONS.map((q, idx) => {
            const ans = getNarrativeAnswer(idx, q);
            return (
              <div key={idx} className="profile-summary-qa profile-summary-qa--answer-only">
                <p className="profile-summary-qa-q">{q}</p>
                <p
                  className={`profile-summary-qa-a${
                    ans ? ' profile-summary-qa-a--narrative' : ' profile-summary-qa-a--empty'
                  }`}
                >
                  {ans || m.common.notAnswered}
                </p>
              </div>
            );
          })}
        </div>
      </div>
      <div className="profile-summary-quiz-section">
        {renderQuizLanding()}
        {renderQuizCollectionLink()}
      </div>
      <div className="profile-summary-actions">
        <button
          type="button"
          className="profile-summary-btn profile-summary-btn--outline"
          onClick={() => setStep(7)}
          disabled={isSubmitting}
        >
          {m.summaryFull.backToFirstSummary}
        </button>
        <button
          type="button"
          className="primary-btn profile-summary-btn profile-summary-btn--submit"
          onClick={handleFinalSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? m.common.submittingData : m.common.confirmSubmit}
        </button>
      </div>
    </div>
  );

  return (
    <div className="app-container">
      <SiteHeader hideLangSwitcher={activeModal !== null}>
        <div className={isDirectQuiz ? 'logo logo--quiz' : 'logo'}>
          {isDirectQuiz ? (
            <>
              {m.logo.quizPrefix}<span>{m.logo.quizHighlight}</span>{m.logo.quizSuffix}
            </>
          ) : (
            m.logo.dating
          )}
        </div>
      </SiteHeader>
      {step > 0 && step < 7 && !isDirectQuiz && (
        <p style={{ textAlign: 'center', marginBottom: '30px', color: 'var(--text-secondary)' }}>
          {m.progress.introHint}
        </p>
      )}

      {step > 0 && step <= 7 && !isDirectQuiz && (
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }}></div>
        </div>
      )}

      {step >= 8 && step <= 12 && (
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${((step - 7) / 5) * 100}%`, background: 'linear-gradient(90deg, #8b5cf6, #ec4899)' }}></div>
        </div>
      )}

      <div className="step-container">
        {step === 0 && (
          <div>
            <h2>{isDirectQuiz ? m.login.titleQuiz : m.login.title}</h2>
            {isDirectQuiz && (
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '16px' }}>
                {m.login.quizHint}{' '}
                <button
                  type="button"
                  onClick={() => setStep(7)}
                  style={{ background: 'none', border: 'none', color: 'var(--accent-color)', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                >
                  {m.login.backToQuiz}
                </button>
              </p>
            )}
            <div className="form-group" style={{ marginTop: '30px' }}>
              <label className="form-label">{m.login.emailLabel} <span style={{ color: 'var(--error-color)' }}>*</span></label>
              <input type="text" name="email" placeholder={m.login.emailPlaceholder} value={formData.email} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">{m.login.pinLabel} <span style={{ color: 'var(--error-color)' }}>*</span></label>
              <input type="password" name="pin" placeholder={m.login.pinPlaceholder} value={formData.pin} onChange={handleChange} />
            </div>
            <button className="primary-btn" onClick={handleNextStep} disabled={!formData.email || formData.pin.length < 6 || isSubmitting} style={{ width: '100%', marginBottom: '15px' }}>
              {isSubmitting ? m.common.pleaseWait : m.common.join}
            </button>
            {!isDirectQuiz && (
              <div className="home-quiz-link">
                <Link to="/quiz">{m.login.tryQuizFirst}</Link>
              </div>
            )}

            {/* Teaser: 10 random cards on homepage (real + mẫu), click any to go to /cards gallery */}
            {recentCards.length > 0 && (
              <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px dashed var(--border-color)' }}>
                <h4 style={{ textAlign: 'center', marginBottom: '8px', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  Thẻ card ngẫu nhiên từ cộng đồng
                </h4>
                <div
                  style={{
                    display: 'flex',
                    gap: '10px',
                    overflowX: 'auto',
                    paddingBottom: '8px',
                    scrollSnapType: 'x mandatory',
                    WebkitOverflowScrolling: 'touch',
                  }}
                >
                  {(recentCards || [])
                    .filter((c: any) => c && c.id)
                    .map((card: any) => {
                      const isSynthetic = !!card.synthetic || !card.image_url;
                      const nick = (card.user && card.user.nickname) || card.display_name || 'User';
                      return (
                        <Link
                          key={card.id}
                          to="/cards"
                          style={{ 
                            display: 'block', 
                            textDecoration: 'none', 
                            minWidth: '68vw', 
                            maxWidth: '280px',
                            flexShrink: 0,
                            scrollSnapAlign: 'center'
                          }}
                          title="Xem tất cả thẻ card"
                        >
                          {(card.result || isSynthetic) ? (
                            <div
                              style={{
                                width: '100%',
                                aspectRatio: '9/16',
                                overflow: 'hidden',
                                borderRadius: '10px',
                                border: '1px solid var(--border-color)',
                                background: '#0a0a0a',
                                pointerEvents: 'none',
                              }}
                            >
                              {/* On homepage teaser, for synthetic (sample) cards show the new personal profile card to make them look like real attractive people */}
                              {isSynthetic && card.user ? (
                                <PersonalProfileCard
                                  data={{
                                    nickname: card.user?.nickname || nick,
                                    city: card.user?.city,
                                    height: card.user?.height,
                                    weight: card.user?.weight,
                                    bodyType: card.user?.bodyType || card.user?.body_type,
                                    job: card.user?.job,
                                    role: card.user?.role,
                                    zodiac: card.user?.zodiac,
                                    rel: card.user?.rel,
                                    guTitle: card.result?.shareTitle,
                                    guHook: card.result?.shareHook,
                                  }}
                                  displayName={nick}
                                  totalLikes={card.total || 0}
                                />
                              ) : (
                                <QuizShareCard
                                  id={`teaser-${card.id}`}
                                  variant={card.quiz_type as any}
                                  aspect="story"
                                  fitContainer
                                  displayWidth={260}
                                  shareTitle={card.result ? card.result.shareTitle : (card.share_title || '')}
                                  shareHook={card.result ? (card.result.shareHook || '') : ''}
                                  percent={card.result ? (card.result.percent || 50) : 50}
                                  typeKey={card.result ? (card.result.typeKey || 'A') : 'A'}
                                  variantKey={card.result ? (card.result.variantKey || card.variant_key) : card.variant_key}
                                  displayName={nick}
                                  city={card.user?.city}
                                  zodiac={card.user?.zodiac?.name}
                                  zodiacSymbol={card.user?.zodiac?.symbol}
                                  stats={card.result ? (card.result.stats || []) : []}
                                  progressBars={card.result ? card.result.progressBars : undefined}
                                />
                              )}
                            </div>
                          ) : (
                            <img
                              src={card.image_url}
                              alt={typeof card.share_title === 'string' ? card.share_title : 'Card'}
                              style={{
                                width: '100%',
                                aspectRatio: '9/16',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                border: '1px solid var(--border-color)',
                                display: 'block',
                              }}
                              loading="lazy"
                            />
                          )}
                        </Link>
                      );
                    })}
                </div>
                <div style={{ textAlign: 'center', marginTop: '4px' }}>
                  <Link to="/cards" style={{ fontSize: '0.75rem', color: 'var(--accent-color)' }}>
                    Xem tất cả thẻ card →
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}

        {step === 1 && (
          <div>
            <h2>{m.steps.step1.title}</h2>
            <p style={{ color: 'var(--accent-color)', fontSize: '0.9rem', marginBottom: '20px', fontStyle: 'italic', opacity: 0.9 }}>
              {m.steps.step1.privacyNote}
            </p>
            <div className="row-group">
              <div className="form-group">
                <label className="form-label">{m.steps.step1.fullName} <span style={{ color: 'var(--error-color)' }}>*</span></label>
                <input type="text" name="fullName" className="input-field" value={formData.fullName} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label className="form-label">{m.steps.step1.nickname}</label>
                <input type="text" name="nickname" className="input-field" value={formData.nickname} onChange={handleChange} />
              </div>
            </div>
            
            <div className="form-group">
              <label className="form-label">{m.steps.step1.dob} <span style={{ color: 'var(--error-color)' }}>*</span></label>
              <input type="text" inputMode="numeric" name="dob" className="input-field" placeholder={m.steps.step1.dobPlaceholder} value={formData.dob} onChange={handleDobChange} />
            </div>

            <div className="form-group">
              <label className="form-label">{m.steps.step1.city} <span style={{ color: 'var(--error-color)' }}>*</span></label>
              <div className="option-grid option-grid--tiles">
                {fo.cities.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    className={`option-btn ${formData.city === opt ? 'selected' : ''}`}
                    onClick={() => handleOptionSelect('city', opt)}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{m.steps.step1.address}</label>
              <input type="text" name="address" className="input-field" placeholder={m.steps.step1.addressPlaceholder} value={formData.address} onChange={handleChange} />
            </div>
            <div className="button-group">
              <button onClick={prevStep} disabled={isSubmitting}>{m.common.back}</button>
              <button className="primary-btn" onClick={handleNextStep} disabled={!formData.fullName || formData.dob.length < 8 || !formData.city || isSubmitting}>
                {isSubmitting ? m.common.pleaseWait : m.common.continue}
              </button>
              {hasReachedSummary && (
                <button onClick={() => { saveToAirtable(recordId, true); setStep(7); }} disabled={!formData.fullName || formData.dob.length < 8 || !formData.city || isSubmitting}
                  style={{ background: 'transparent', border: '1px solid var(--accent-color)', color: 'var(--accent-color)' }}>
                  Về Tổng hợp
                </button>
              )}
            </div>
          </div>
        )}

                {step === 2 && (
          <div>
            <h2>{m.steps.step2.title}</h2>
            <div className="metric-row">
              <div className="form-group form-group--inline">
                <label className="form-label">{m.steps.step2.height} <span style={{ color: 'var(--error-color)' }}>*</span></label>
                <input type="number" inputMode="numeric" name="height" className="input-field input-field--metric" placeholder={m.steps.step2.heightPlaceholder} value={formData.height} onChange={handleChange} />
              </div>
              <div className="form-group form-group--inline">
                <label className="form-label">{m.steps.step2.weight}</label>
                <input type="number" inputMode="numeric" name="weight" className="input-field input-field--metric" placeholder={m.steps.step2.weightPlaceholder} value={formData.weight} onChange={handleChange} />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{m.steps.step2.bodyType}</label>
              <div className="option-grid">
                {fo.bodyTypes.map(opt => (
                  <button key={opt} type="button" className={`option-btn ${isSameOption(formData.bodyType, opt) ? 'selected' : ''}`} onClick={() => handleOptionSelect('bodyType', opt)}>
                    {renderOptionText(opt)}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{m.steps.step2.education}</label>
              <div className="option-grid">
                {fo.education.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    className={`option-btn ${formData.education === opt ? 'selected' : ''}`}
                    onClick={() => handleOptionSelect('education', opt)}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{m.steps.step2.income} <span style={{ color: 'var(--error-color)' }}>*</span></label>
              <div className="option-grid">
                {fo.income.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    className={`option-btn ${formData.income === opt ? 'selected' : ''}`}
                    onClick={() => handleOptionSelect('income', opt)}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{m.steps.step2.job} <span style={{ color: 'var(--error-color)' }}>*</span></label>
              <input type="text" name="job" className="input-field input-field--compact" placeholder={m.steps.step2.jobPlaceholder} value={formData.job} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label className="form-label">{m.steps.step2.currentStatus} <span style={{ color: 'var(--error-color)' }}>*</span></label>
              <div className="option-grid">
                {fo.currentStatus.map(opt => (
                  <button key={opt} className={`option-btn ${formData.currentStatus === opt ? 'selected' : ''}`} onClick={() => handleOptionSelect('currentStatus', opt)}>
                    {renderOptionText(opt)}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{m.steps.step2.livingSpace}</label>
              <div className="option-grid">
                {fo.livingSpace.map(opt => (
                  <button key={opt} className={`option-btn ${formData.livingSpace === opt ? 'selected' : ''}`} onClick={() => handleOptionSelect('livingSpace', opt)}>
                    {renderOptionText(opt)}
                  </button>
                ))}
              </div>
            </div>

            <div className="button-group">
              <button onClick={prevStep} disabled={isSubmitting}>{m.common.back}</button>
              <button className="primary-btn" onClick={handleNextStep} disabled={!formData.height || !formData.job || !formData.income || !formData.currentStatus || isSubmitting}>
                {isSubmitting ? m.common.pleaseWait : m.common.continue}
              </button>
              {hasReachedSummary && (
                <button onClick={() => { saveToAirtable(recordId, true); setStep(7); }} disabled={!formData.height || !formData.job || !formData.income || !formData.currentStatus || isSubmitting}
                  style={{ background: 'transparent', border: '1px solid var(--accent-color)', color: 'var(--accent-color)' }}>
                  Về Tổng hợp
                </button>
              )}
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <h2>{m.steps.step3.title}</h2>
            
            <div className="form-group">
              <label className="form-label">{m.steps.step3.orientation} <span style={{ color: 'var(--error-color)' }}>*</span></label>
              <div className="option-grid">
                {fo.orientation.map(opt => (
                  <button key={opt} className={`option-btn ${formData.orientation === opt ? 'selected' : ''}`} onClick={() => handleOptionSelect('orientation', opt)}>
                    {renderOptionText(opt)}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{m.steps.step3.role} <span style={{ color: 'var(--error-color)' }}>*</span></label>
              <div className="tag-grid">
                {fo.role.map(opt => (
                  <button key={opt} className={`tag-btn ${formData.role.includes(opt) ? 'selected' : ''}`} onClick={() => toggleMultiSelect('role', opt)}>
                    {renderOptionText(opt)}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{m.steps.step3.comingOut}</label>
              <div className="option-grid">
                {fo.comingOut.map(opt => (
                  <button key={opt} className={`option-btn ${formData.comingOut === opt ? 'selected' : ''}`} onClick={() => handleOptionSelect('comingOut', opt)}>
                    {renderOptionText(opt)}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{m.steps.step3.relationship} <span style={{ color: 'var(--error-color)' }}>*</span></label>
              <div className="option-grid">
                {fo.rel.map(opt => (
                  <button key={opt} className={`option-btn ${formData.rel.includes(opt) ? 'selected' : ''}`} onClick={() => toggleMultiSelect('rel', opt)}>
                    {renderOptionText(opt)}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{m.steps.step3.personality}</label>
              <div className="tag-grid">
                {fo.personality.map(opt => (
                  <button key={opt} className={`tag-btn ${formData.personality.includes(opt) ? 'selected' : ''}`} onClick={() => toggleMultiSelect('personality', opt)}>
                    {renderOptionText(opt)}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{m.steps.step3.pros}</label>
              <div className="tag-grid">
                {fo.pros.map(opt => (
                  <button key={opt} className={`tag-btn ${formData.pros.includes(opt) ? 'selected' : ''}`} onClick={() => toggleMultiSelect('pros', opt)}>
                    {renderOptionText(opt)}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{m.steps.step3.cons}</label>
              <div className="tag-grid">
                {fo.cons.map(opt => (
                  <button key={opt} className={`tag-btn ${formData.cons.includes(opt) ? 'selected' : ''}`} onClick={() => toggleMultiSelect('cons', opt)}>
                    {renderOptionText(opt)}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{m.steps.step3.substances}</label>
              <div className="tag-grid">
                {fo.substances.map(opt => (
                  <button key={opt} className={`tag-btn ${formData.substances.includes(opt) ? 'selected' : ''}`} onClick={() => toggleMultiSelect('substances', opt)}>
                    {renderOptionText(opt)}
                  </button>
                ))}
              </div>
            </div>

            <div className="button-group">
              <button onClick={prevStep} disabled={isSubmitting}>{m.common.back}</button>
              <button className="primary-btn" onClick={handleNextStep} disabled={!formData.orientation || formData.role.length === 0 || formData.rel.length === 0 || isSubmitting}>
                {isSubmitting ? m.common.pleaseWait : m.common.continue}
              </button>
              {hasReachedSummary && (
                <button onClick={() => { saveToAirtable(recordId, true); setStep(7); }} disabled={!formData.orientation || formData.role.length === 0 || formData.rel.length === 0 || isSubmitting}
                  style={{ background: 'transparent', border: '1px solid var(--accent-color)', color: 'var(--accent-color)' }}>
                  Về Tổng hợp
                </button>
              )}
            </div>
          </div>
        )}

        {step === 4 && (
          <div>
            <h2>{m.steps.step4.title}</h2>
            
            <div className="form-group">
              <label className="form-label">{m.steps.step4.energy} <span style={{ color: 'var(--error-color)' }}>*</span></label>
              <div className="option-grid">
                {fo.energy.map(opt => (
                  <button key={opt} className={`option-btn ${formData.energy === opt ? 'selected' : ''}`} onClick={() => handleOptionSelect('energy', opt)}>
                    {renderOptionText(opt)}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{m.steps.step4.lifestyle}</label>
              <div className="option-grid">
                {fo.lifestyle.map(opt => (
                  <button key={opt} className={`option-btn ${formData.lifestyle === opt ? 'selected' : ''}`} onClick={() => handleOptionSelect('lifestyle', opt)}>
                    {renderOptionText(opt)}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{m.steps.step4.hobbies}</label>
              <div className="tag-grid">
                {fo.hobbies.map(opt => (
                  <button key={opt} className={`tag-btn ${formData.hobbies.includes(opt) ? 'selected' : ''}`} onClick={() => toggleMultiSelect('hobbies', opt)}>
                    {renderOptionText(opt)}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="form-group">
              <label className="form-label">{m.steps.step4.loveLanguage}</label>
              <div className="tag-grid">
                {fo.love.map(opt => (
                  <button key={opt} className={`tag-btn ${formData.love.includes(opt) ? 'selected' : ''}`} onClick={() => toggleMultiSelect('love', opt)}>
                    {renderOptionText(opt)}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{m.steps.step4.futurePlans}</label>
              <div className="tag-grid">
                {fo.futurePlans.map(opt => (
                  <button key={opt} className={`tag-btn ${formData.futurePlans.includes(opt) ? 'selected' : ''}`} onClick={() => toggleMultiSelect('futurePlans', opt)}>
                    {renderOptionText(opt)}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{m.steps.step4.futureKids}</label>
              <div className="option-grid">
                {fo.futureKids.map(opt => (
                  <button key={opt} className={`option-btn ${formData.futureKids === opt ? 'selected' : ''}`} onClick={() => handleOptionSelect('futureKids', opt)}>
                    {renderOptionText(opt)}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{m.steps.step4.financeShared}</label>
              <div className="tag-grid">
                {fo.financeShared.map(opt => (
                  <button key={opt} className={`tag-btn ${formData.financeShared.includes(opt) ? 'selected' : ''}`} onClick={() => toggleMultiSelect('financeShared', opt)}>
                    {renderOptionText(opt)}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{m.steps.step4.financeMindset}</label>
              <div className="tag-grid">
                {fo.financeMindset.map(opt => (
                  <button key={opt} className={`tag-btn ${formData.financeMindset.includes(opt) ? 'selected' : ''}`} onClick={() => toggleMultiSelect('financeMindset', opt)}>
                    {renderOptionText(opt)}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{m.steps.step4.sexView}</label>
              <div className="tag-grid">
                {fo.sexView.map(opt => (
                  <button key={opt} className={`tag-btn ${formData.sexView.includes(opt) ? 'selected' : ''}`} onClick={() => toggleMultiSelect('sexView', opt)}>
                    {renderOptionText(opt)}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="button-group">
              <button onClick={prevStep} disabled={isSubmitting}>{m.common.back}</button>
              <button className="primary-btn" onClick={handleNextStep} disabled={!formData.energy || isSubmitting}>
                {isSubmitting ? m.common.pleaseWait : m.common.continue}
              </button>
              {hasReachedSummary && (
                <button onClick={() => { saveToAirtable(recordId, true); setStep(7); }} disabled={!formData.energy || isSubmitting}
                  style={{ background: 'transparent', border: '1px solid var(--accent-color)', color: 'var(--accent-color)' }}>
                  Về Tổng hợp
                </button>
              )}
            </div>
          </div>
        )}

        {step === 5 && (
          <div>
            <h2>{m.steps.step5.title}</h2>
            
            <div className="form-group">
              <label className="form-label">{m.steps.step5.partnerAge} <span style={{ color: 'var(--error-color)' }}>*</span></label>
              <div className="tag-grid">
                {fo.age.map(opt => (
                  <button key={opt} className={`tag-btn ${formData.age.includes(opt) ? 'selected' : ''}`} onClick={() => toggleMultiSelect('age', opt)}>
                    {renderOptionText(opt)}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{m.steps.step5.partnerRole}</label>
              <div className="tag-grid">
                {fo.partnerRole.map(opt => (
                  <button key={opt} className={`tag-btn ${formData.partnerRole.includes(opt) ? 'selected' : ''}`} onClick={() => toggleMultiSelect('partnerRole', opt)}>
                    {renderOptionText(opt)}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{m.steps.step5.partnerBody}</label>
              <div className="tag-grid">
                {fo.partnerBody.map(opt => (
                  <button key={opt} className={`tag-btn ${formData.partnerBody.includes(opt) ? 'selected' : ''}`} onClick={() => toggleMultiSelect('partnerBody', opt)}>
                    {renderOptionText(opt)}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{m.steps.step5.priority}</label>
              <div className="option-grid">
                {fo.priority.map(opt => (
                  <button key={opt} className={`option-btn ${formData.priority === opt ? 'selected' : ''}`} onClick={() => handleOptionSelect('priority', opt)}>
                    {renderOptionText(opt)}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{m.steps.step5.redflag}</label>
              <div className="tag-grid">
                {fo.redflag.map(opt => (
                  <button key={opt} className={`tag-btn ${formData.redflag.includes(opt) ? 'selected' : ''}`} onClick={() => toggleMultiSelect('redflag', opt)}>
                    {renderOptionText(opt)}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{m.steps.step5.messageToAdmin}</label>
              <textarea name="messageToAdmin" rows={4} placeholder={m.steps.step5.messageToAdminPlaceholder} value={formData.messageToAdmin} onChange={handleChange}></textarea>
            </div>

            <div className="button-group">
              <button onClick={prevStep} disabled={isSubmitting}>{m.common.back}</button>
              <button className="primary-btn" onClick={handleNextStep} disabled={formData.age.length === 0 || isSubmitting}>
                {isSubmitting ? m.common.pleaseWait : m.common.continue}
              </button>
              {hasReachedSummary && (
                <button onClick={() => { saveToAirtable(recordId, true); setStep(7); }} disabled={formData.age.length === 0 || isSubmitting}
                  style={{ background: 'transparent', border: '1px solid var(--accent-color)', color: 'var(--accent-color)' }}>
                  Về Tổng hợp
                </button>
              )}
            </div>
          </div>
        )}

        {step === 6 && (
          <div>
            <h2>{m.steps.step6.title}</h2>

            <div className="form-group">
              <label className="form-label" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {m.steps.step6.avatar} <span style={{ color: 'var(--error-color)' }}>*</span>
                <label className="upload-btn">
                  {isUploading.avatar ? m.steps.step6.avatarUploading : m.steps.step6.avatarUpload}
                  <input type="file" multiple accept="image/*" onChange={(e) => handleFileChange(e, 'avatar')} style={{ display: 'none' }} disabled={isUploading.avatar} />
                </label>
                {formData.avatar.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '1rem' }}>
                    {formData.avatar.map((url, i) => (
                      <div key={i} style={{ position: 'relative', width: '80px', height: '80px' }}>
                        <img src={url} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }} />
                        <button onClick={(e) => { e.preventDefault(); removeAvatar(i); }} style={{ position: 'absolute', top: '-5px', right: '-5px', background: 'var(--error-color)', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', padding: 0, fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </label>
            </div>

            <div className="form-group">
              <label className="form-label">{m.steps.step6.socialLink} <span style={{ color: 'var(--error-color)' }}>*</span></label>
              <input type="text" name="socialLink" placeholder={m.steps.step6.socialLinkPlaceholder} value={formData.socialLink} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label className="form-label" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {m.steps.step6.idCard}
                <span className="subtitle">{m.steps.step6.idCardSubtitle}</span>
                <label className="upload-btn">
                  {isUploading.idCard ? m.steps.step6.idCardUploading : (formData.idCard ? m.steps.step6.idCardUploaded : m.steps.step6.idCardUpload)}
                  <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, 'idCard')} style={{ display: 'none' }} disabled={isUploading.idCard} />
                </label>
                {formData.idCard && !isUploading.idCard && (
                  <img src={formData.idCard} alt="Preview" style={{ width: '100%', maxHeight: '200px', objectFit: 'contain', marginTop: '1rem', borderRadius: '8px' }} />
                )}
              </label>
            </div>

            <div className="button-group">
              {!hasReachedSummary && <button onClick={prevStep} disabled={isSubmitting}>{m.common.back}</button>}
              <button className="primary-btn" onClick={handleNextStep} disabled={!formData.socialLink || formData.avatar.length === 0 || isSubmitting}>
                {isSubmitting ? m.common.pleaseWait : m.common.continue}
              </button>
            </div>
          </div>
        )}

        {step === 7 && (
          isDirectQuiz && !showFullSummary ? (
            <div>
              <div className="quiz-hero">
                <h2>{m.quiz.heroTitle}</h2>
                <p>
                  {m.quiz.heroDescription}
                </p>
                <p className="quiz-hero-note">{m.quiz.heroNote}</p>
              </div>

              {formData.fullName && (
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '20px', textAlign: 'center' }}>
                  {m.quiz.loggedInAs} <strong style={{ color: 'var(--text-primary)' }}>{formData.fullName}</strong>
                  {' · '}
                  <button type="button" onClick={() => setShowFullSummary(true)} style={{ background: 'none', border: 'none', color: 'var(--accent-color)', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: 'inherit' }}>
                    {m.quiz.viewProfile}
                  </button>
                </p>
              )}

              {renderQuizLanding()}

              <div className="quiz-footer-links">
                {isGuestOnQuiz ? (
                  <>
                    <button type="button" onClick={goToCreateProfile}>
                      {m.quiz.createPrivateProfile}
                    </button>
                    <button type="button" onClick={() => setStep(0)}>
                      {m.quiz.alreadyHaveProfile}
                    </button>
                  </>
                ) : (
                  <button type="button" onClick={handleFinalSubmit} disabled={isSubmitting}>
                    {isSubmitting ? m.common.submitting : m.quiz.saveQuizToProfile}
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div>
              {isDirectQuiz && showFullSummary && (
                <button 
                  onClick={() => setShowFullSummary(false)} 
                  style={{
                    marginBottom: '20px',
                    padding: '10px 20px',
                    background: 'transparent',
                    border: '1px solid var(--border-color)',
                    color: 'var(--accent-color)',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {m.quiz.backToQuizPage}
                </button>
              )}

              {renderStep7FullSummary()}
            </div>
          )
        )}

        {/* BƯỚC 8: Tâm lý */}
        {step === 8 && (
          <div>
            <h2>{m.steps.step8.title}</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '20px' }}>{m.steps.step8.description}</p>
            {EXTRA_DETAILS_QUESTIONS.filter(q => q.sectionKey === 'psych').map((q, idx) => (
              <React.Fragment key={q.id}>
                {idx > 0 && <hr style={{ border: 'none', borderTop: '1px dashed rgba(255,255,255,0.1)', margin: '20px 0' }} />}
                <div className="form-group">
                  <label className="form-label">{idx + 1}. {q.q}</label>
                  <div className="option-grid">
                    {q.options.map(opt => (
                      <button
                        key={opt}
                        className={`option-btn ${isExtraOptionSelected(extraAnswerKey(q.id), opt, q.q) ? 'selected' : ''}`}
                        onClick={() => handleExtraOptionSelect(extraAnswerKey(q.id), opt, q.type === 'checkbox', undefined, q.q)}
                      >
                        {renderQuizOptionText(opt)}
                      </button>
                    ))}
                  </div>
                </div>
              </React.Fragment>
            ))}
            <div className="button-group">
              <button onClick={() => setStep(7)} disabled={isSubmitting}>{m.common.backToSummary}</button>
              <button className="primary-btn" onClick={handleNextStep} disabled={isSubmitting}>
                {isSubmitting ? m.common.pleaseWait : m.common.continue}
              </button>
              {hasReachedFinalSummary && (
                <button onClick={() => { saveToAirtable(recordId, true); setStep(13); }} disabled={isSubmitting}
                  style={{ background: 'transparent', border: '1px solid var(--accent-color)', color: 'var(--accent-color)' }}>
                  Về Tổng hợp cuối
                </button>
              )}
            </div>
          </div>
        )}

        {/* BƯỚC 9: Thực tế cuộc sống */}
        {step === 9 && (
          <div>
            <h2>{m.steps.step9.title}</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '20px' }}>{m.steps.step9.description}</p>
            {EXTRA_DETAILS_QUESTIONS.filter(q => q.sectionKey === 'practical').map((q, idx) => (
              <React.Fragment key={q.id}>
                {idx > 0 && <hr style={{ border: 'none', borderTop: '1px dashed rgba(255,255,255,0.1)', margin: '20px 0' }} />}
                <div className="form-group">
                  <label className="form-label">{idx + 1}. {q.q}</label>
                  <div className="option-grid">
                    {q.options.map(opt => (
                      <button
                        key={opt}
                        className={`option-btn ${isExtraOptionSelected(extraAnswerKey(q.id), opt, q.q) ? 'selected' : ''}`}
                        onClick={() => handleExtraOptionSelect(extraAnswerKey(q.id), opt, q.type === 'checkbox', undefined, q.q)}
                      >
                        {renderQuizOptionText(opt)}
                      </button>
                    ))}
                  </div>
                </div>
              </React.Fragment>
            ))}
            <div className="button-group">
              <button onClick={prevStep} disabled={isSubmitting}>{m.common.back}</button>
              <button className="primary-btn" onClick={handleNextStep} disabled={isSubmitting}>
                {isSubmitting ? m.common.pleaseWait : m.common.continue}
              </button>
              {hasReachedFinalSummary && (
                <button onClick={() => { saveToAirtable(recordId, true); setStep(13); }} disabled={isSubmitting}
                  style={{ background: 'transparent', border: '1px solid var(--accent-color)', color: 'var(--accent-color)' }}>
                  Về Tổng hợp cuối
                </button>
              )}
            </div>
          </div>
        )}

        {/* BƯỚC 10: Tình dục & Sức khỏe */}
        {step === 10 && (
          <div>
            <h2>{m.steps.step10.title}</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '20px' }}>{m.steps.step10.description}</p>
            {EXTRA_DETAILS_QUESTIONS.filter(q => q.sectionKey === 'sex').map((q, idx) => (
              <React.Fragment key={q.id}>
                {idx > 0 && <hr style={{ border: 'none', borderTop: '1px dashed rgba(255,255,255,0.1)', margin: '20px 0' }} />}
                <div className="form-group">
                  <label className="form-label">{idx + 1}. {q.q}</label>
                  <div className="option-grid">
                    {q.options.map(opt => (
                      <button
                        key={opt}
                        className={`option-btn ${isExtraOptionSelected(extraAnswerKey(q.id), opt, q.q) ? 'selected' : ''}`}
                        onClick={() => handleExtraOptionSelect(extraAnswerKey(q.id), opt, q.type === 'checkbox', undefined, q.q)}
                      >
                        {renderQuizOptionText(opt)}
                      </button>
                    ))}
                  </div>
                </div>
              </React.Fragment>
            ))}
            <div className="button-group">
              <button onClick={prevStep} disabled={isSubmitting}>{m.common.back}</button>
              <button className="primary-btn" onClick={handleNextStep} disabled={isSubmitting}>
                {isSubmitting ? m.common.pleaseWait : m.common.continue}
              </button>
              {hasReachedFinalSummary && (
                <button onClick={() => { saveToAirtable(recordId, true); setStep(13); }} disabled={isSubmitting}
                  style={{ background: 'transparent', border: '1px solid var(--accent-color)', color: 'var(--accent-color)' }}>
                  Về Tổng hợp cuối
                </button>
              )}
            </div>
          </div>
        )}

        {/* BƯỚC 11: Áp lực gia đình */}
        {step === 11 && (
          <div>
            <h2>{m.steps.step11.title}</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '20px' }}>{m.steps.step11.description}</p>
            {EXTRA_DETAILS_QUESTIONS.filter(q => q.sectionKey === 'pressure').map((q, idx) => (
              <React.Fragment key={q.id}>
                {idx > 0 && <hr style={{ border: 'none', borderTop: '1px dashed rgba(255,255,255,0.1)', margin: '20px 0' }} />}
                <div className="form-group">
                  <label className="form-label">{idx + 1}. {q.q}</label>
                  <div className="option-grid">
                    {q.options.map(opt => (
                      <button
                        key={opt}
                        className={`option-btn ${isExtraOptionSelected(extraAnswerKey(q.id), opt, q.q) ? 'selected' : ''}`}
                        onClick={() => handleExtraOptionSelect(extraAnswerKey(q.id), opt, q.type === 'checkbox', undefined, q.q)}
                      >
                        {renderQuizOptionText(opt)}
                      </button>
                    ))}
                  </div>
                </div>
              </React.Fragment>
            ))}
            <div className="button-group">
              <button onClick={prevStep} disabled={isSubmitting}>{m.common.back}</button>
              <button className="primary-btn" onClick={handleNextStep} disabled={isSubmitting}>
                {isSubmitting ? m.common.pleaseWait : m.common.continue}
              </button>
              {hasReachedFinalSummary && (
                <button onClick={() => { saveToAirtable(recordId, true); setStep(13); }} disabled={isSubmitting}
                  style={{ background: 'transparent', border: '1px solid var(--accent-color)', color: 'var(--accent-color)' }}>
                  Về Tổng hợp cuối
                </button>
              )}
            </div>
          </div>
        )}

        {/* BƯỚC 12: Câu hỏi tự luận EQ */}
        {step === 12 && (
          <div>
            <h2>{m.steps.step12.title}</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '20px' }}>{m.steps.step12.description}</p>
            {NARRATIVE_QUESTIONS.map((q, idx) => (
              <React.Fragment key={idx}>
                {idx > 0 && <hr style={{ border: 'none', borderTop: '1px dashed rgba(255,255,255,0.1)', margin: '20px 0' }} />}
                <div className="form-group">
                  <label className="form-label">{idx + 1}. {q}</label>
                  <textarea
                    placeholder={m.steps.step12.answerPlaceholder}
                    rows={4}
                    value={getNarrativeAnswer(idx, q)}
                    onChange={(e) => handleExtraAnswerChange(narrativeAnswerKey(idx), e.target.value)}
                    style={{
                      width: '100%', padding: '12px',
                      background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-color)',
                      borderRadius: '8px', color: '#fff', fontSize: '0.9rem', lineHeight: 1.6, resize: 'vertical'
                    }}
                  />
                </div>
              </React.Fragment>
            ))}
            <div className="button-group">
              <button onClick={prevStep} disabled={isSubmitting}>{m.common.back}</button>
              <button className="primary-btn" onClick={handleNextStep} disabled={isSubmitting}>
                {isSubmitting ? m.common.pleaseWait : m.common.viewFinalSummary}
              </button>
            </div>
          </div>
        )}

        {/* BƯỚC 13: Tổng hợp toàn diện */}
        {step === 13 && <div>{renderStep13FullSummary()}</div>}

        {/* BƯỚC 14: Thành công */}
        {step === 14 && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: '3rem', marginBottom: '20px' }}>✧</div>
            <h2 style={{ color: 'var(--accent-color)' }}>{m.success.title}</h2>
            <p style={{ marginTop: '20px', lineHeight: 1.8 }}>
              {m.success.body}
            </p>
            <p style={{ marginTop: '10px', color: 'var(--text-secondary)' }}>
              {m.success.hint} <strong>{m.success.hintCredentials}</strong> {m.success.hintSuffix}
            </p>
            <button className="primary-btn" onClick={() => setStep(0)} style={{ marginTop: '30px' }}>
              {m.success.backHome}
            </button>
          </div>
        )}

        {step > 0 && step <= 13 && (
          <button className="logout-btn" onClick={handleExit}>{m.common.exit}</button>
        )}



        {/* Modal 2: Trắc Nghiệm Tính Cách Hẹn Hò */}
        {activeModal === 'personality' && createPortal(
          <div className="quiz-modal-overlay">
            <div
              className={`quiz-modal${
                formData.extraDetails?.personalityResult ? ' quiz-modal--fullscreen-mobile' : ''
              }`}
            >
              <div className="quiz-modal-header">
                <h3>{m.quiz.personality.title}</h3>
                <button type="button" className="quiz-modal-close" onClick={() => setActiveModal(null)} aria-label={m.quiz.closeAriaLabel}>&times;</button>
              </div>

              {!formData.extraDetails?.personalityResult ? (
                <div>
                  <div className="quiz-progress-text">
                    {formatMessage(m.quiz.progress, { current: String(personalityQuizIndex + 1), total: String(PERSONALITY_QUIZ.length) })}
                  </div>
                  <div className="quiz-progress-bar">
                    <div className="quiz-progress-fill quiz-progress-fill--gold" style={{ width: `${((personalityQuizIndex + 1) / PERSONALITY_QUIZ.length) * 100}%` }} />
                  </div>
                  
                  <h4 className="quiz-question">
                    {PERSONALITY_QUIZ[personalityQuizIndex].q}
                  </h4>

                  <div className="quiz-option-list">
                    {PERSONALITY_QUIZ[personalityQuizIndex].options.map((opt, i) => (
                      <button 
                        key={`p-${personalityQuizIndex}-${i}`}
                        type="button"
                        className="quiz-option-btn"
                        onClick={(e) => handlePersonalityAnswer(opt.type, e)}
                      >
                        {opt.text}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="quiz-result-panel">
                  {formData.extraDetails.personalityResult && (
                    <>
                      <QuizShareNameField
                        value={shareDisplayName}
                        onChange={handleShareDisplayNameChange}
                        autoFocus
                        showRequiredHint
                      />
                      {canChooseShareAvatar && (
                        <QuizShareAvatarPicker
                          value={shareAvatarMode}
                          onChange={handleShareAvatarModeChange}
                          personalPreviewUrl={formData.avatar[0]}
                          defaultEmoji="🎭"
                          tone="gold"
                        />
                      )}
                      <QuizShareAspectPicker
                        value={shareCardAspect}
                        onChange={handleShareAspectChange}
                        tone="gold"
                      />
                      <div
                        className="quiz-share-card-preview"
                        style={{
                          width: shareCardSpec.previewWidth,
                          height: shareCardSpec.previewHeight,
                        }}
                      >
                        <QuizShareCard
                          id="personality-result-card"
                          variant="personality"
                          aspect={shareCardAspect}
                          shareTitle={formData.extraDetails.personalityResult.shareTitle}
                          shareHook={formData.extraDetails.personalityResult.shareHook}
                          percent={formData.extraDetails.personalityResult.percent}
                          typeKey={formData.extraDetails.personalityResult.typeKey}
                          variantKey={formData.extraDetails.personalityResult.variantKey}
                          displayName={shareDisplayName.trim() || undefined}
                          city={shareProfileMeta.city}
                          zodiac={shareProfileMeta.zodiac}
                          zodiacSymbol={shareProfileMeta.zodiacSymbol}
                          stats={getShareCardStats('personality', formData.extraDetails.personalityResult.typeKey, lang)}
                          avatarUrl={shareCardAvatarUrl}
                        />
                      </div>
                      <p className="quiz-result-desc">
                        {formData.extraDetails.personalityResult.desc}
                      </p>
                    </>
                  )}

                  {captionCopied && (
                    <p style={{ color: '#10b981', fontSize: '0.85rem', margin: 0 }}>{m.share.actions.captionCopied}</p>
                  )}

                  <div className="quiz-result-actions">
                    <button
                      type="button"
                      className="quiz-btn quiz-btn--primary-gold"
                      onClick={() => handleShareImage('personality-result-card', `tinh-cach-hen-ho-${shareCardSpec.filenameSuffix}`, 'personality')}
                      disabled={isGeneratingImage || !shareDisplayName.trim()}
                    >
                      {isGeneratingImage
                        ? m.share.actions.generating
                        : supportsNativeShare
                          ? m.share.actions.share
                          : formatMessage(m.share.actions.downloadWithSpec, {
                              ratio: shareCardSpec.ratioLabel,
                              exportLabel: shareCardSpec.exportLabel,
                            })}
                    </button>
                    {supportsNativeShare && (
                      <p className="quiz-share-hint">
                        {m.share.actions.nativeShareHintPrefix} <strong>{m.share.actions.nativeShareSave}</strong> / <strong>{m.share.actions.nativeShareGooglePhotos}</strong> / <strong>{m.share.actions.nativeShareInstagram}</strong> {m.share.actions.nativeShareHintSuffix}
                      </p>
                    )}
                    {supportsNativeShare && (
                      <button
                        type="button"
                        className="quiz-btn quiz-btn--outline quiz-btn--outline-gold"
                        onClick={() => handleShareImage('personality-result-card', `tinh-cach-hen-ho-${shareCardSpec.filenameSuffix}`, 'personality', true)}
                        disabled={isGeneratingImage || !shareDisplayName.trim()}
                      >
                        {m.share.actions.download}
                      </button>
                    )}
                    <button
                      type="button"
                      className="quiz-btn quiz-btn--outline quiz-btn--outline-gold"
                      onClick={() => copyShareCaption('personality')}
                    >
                      {m.share.actions.copyCaption}
                    </button>
                    {isGuestOnQuiz && (
                      <button
                        type="button"
                        className="quiz-btn quiz-btn--primary-gold"
                        onClick={() => { setActiveModal(null); goToCreateProfile(); }}
                      >
                        {m.quiz.joinDating}
                      </button>
                    )}
                    <button
                      type="button"
                      className="quiz-btn quiz-btn--outline"
                      onClick={() => setActiveModal(null)}
                    >
                      Đóng
                    </button>
                  </div>
                </div>
              )}

            </div>
          </div>,
          document.body
        )}

        {activeModal === 'single' && createPortal(
          <div className="quiz-modal-overlay">
            <div
              className={`quiz-modal${
                formData.extraDetails?.singleResult ? ' quiz-modal--fullscreen-mobile' : ''
              }`}
            >
              <div className="quiz-modal-header">
                <h3 style={{ color: '#7dd3fc' }}>{m.quiz.frozenHeart.title}</h3>
                <button type="button" className="quiz-modal-close" onClick={() => setActiveModal(null)} aria-label={m.quiz.closeAriaLabel}>&times;</button>
              </div>

              {!formData.extraDetails?.singleResult ? (
                singleQuizIndex < 0 ? (
                  <div className="quiz-intro">
                    <p className="quiz-intro-eyebrow">{FROZEN_HEART_META.subtitle}</p>
                    <h4 className="quiz-intro-title">{FROZEN_HEART_META.title}</h4>
                    <p className="quiz-intro-text">{FROZEN_HEART_META.intro}</p>
                    <ul className="quiz-intro-notices">
                      {FROZEN_HEART_META.notices.map((notice) => (
                        <li key={notice}>{notice}</li>
                      ))}
                    </ul>
                    <button type="button" className="quiz-btn quiz-btn--primary-ice" onClick={() => setSingleQuizIndex(0)}>
                      {m.quiz.frozenHeart.decodeNow}
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="quiz-progress-text">
                      {formatMessage(m.quiz.progress, { current: String(singleQuizIndex + 1), total: String(SINGLE_QUIZ.length) })}
                    </div>
                    <div className="quiz-progress-bar">
                      <div className="quiz-progress-fill quiz-progress-fill--ice" style={{ width: `${((singleQuizIndex + 1) / SINGLE_QUIZ.length) * 100}%` }} />
                    </div>
                    
                    <h4 className="quiz-question">
                      {SINGLE_QUIZ[singleQuizIndex].q}
                    </h4>

                    <div className="quiz-option-list">
                      {SINGLE_QUIZ[singleQuizIndex].options.map((opt, i) => (
                        <button 
                          key={`s-${singleQuizIndex}-${i}`}
                          type="button"
                          className="quiz-option-btn quiz-option-btn--ice"
                          onClick={(e) => handleSingleAnswer(opt, e)}
                        >
                          {opt.text}
                        </button>
                      ))}
                    </div>
                  </div>
                )
              ) : (
                <div className="quiz-result-panel">
                  {formData.extraDetails.singleResult && (
                    <>
                      <QuizShareNameField
                        value={shareDisplayName}
                        onChange={handleShareDisplayNameChange}
                        autoFocus
                        showRequiredHint
                      />
                      {canChooseShareAvatar && (
                        <QuizShareAvatarPicker
                          value={shareAvatarMode}
                          onChange={handleShareAvatarModeChange}
                          personalPreviewUrl={formData.avatar[0]}
                          defaultEmoji="🧊"
                          tone="ice"
                        />
                      )}
                      <QuizShareAspectPicker
                        value={shareCardAspect}
                        onChange={handleShareAspectChange}
                        tone="ice"
                      />
                      <div
                        className="quiz-share-card-preview"
                        style={{
                          width: shareCardSpec.previewWidth,
                          height: shareCardSpec.previewHeight,
                        }}
                      >
                        <QuizShareCard
                          id="single-result-card"
                          variant="single"
                          aspect={shareCardAspect}
                          shareTitle={formData.extraDetails.singleResult.shareTitle}
                          shareHook={formData.extraDetails.singleResult.shareHook}
                          percent={formData.extraDetails.singleResult.percent}
                          typeKey={formData.extraDetails.singleResult.typeKey}
                          variantKey={formData.extraDetails.singleResult.variantKey}
                          temperature={formData.extraDetails.singleResult.temperature}
                          temperatureLabel={formData.extraDetails.singleResult.temperatureLabel}
                          triggerTagD={formData.extraDetails.singleResult.triggerTagD}
                          triggerTagDBadge={FROZEN_HEART_TAG_D.cardBadge}
                          displayName={shareDisplayName.trim() || undefined}
                          city={shareProfileMeta.city}
                          zodiac={shareProfileMeta.zodiac}
                          zodiacSymbol={shareProfileMeta.zodiacSymbol}
                          stats={getShareCardStats('single', formData.extraDetails.singleResult.typeKey, lang)}
                          avatarUrl={shareCardAvatarUrl}
                        />
                      </div>
                      <p className="quiz-result-desc" style={{ whiteSpace: 'pre-line' }}>
                        {formData.extraDetails.singleResult.desc}
                      </p>
                    </>
                  )}

                  {captionCopied && (
                    <p style={{ color: '#10b981', fontSize: '0.85rem', margin: 0 }}>{m.share.actions.captionCopied}</p>
                  )}

                  <div className="quiz-result-actions">
                    <button
                      type="button"
                      className="quiz-btn quiz-btn--primary-ice"
                      onClick={() => handleShareImage('single-result-card', `trai-tim-bang-gia-${shareCardSpec.filenameSuffix}`, 'single')}
                      disabled={isGeneratingImage || !shareDisplayName.trim()}
                    >
                      {isGeneratingImage
                        ? m.share.actions.generating
                        : supportsNativeShare
                          ? m.share.actions.share
                          : formatMessage(m.share.actions.downloadWithSpec, {
                              ratio: shareCardSpec.ratioLabel,
                              exportLabel: shareCardSpec.exportLabel,
                            })}
                    </button>
                    {supportsNativeShare && (
                      <p className="quiz-share-hint">
                        {m.share.actions.nativeShareHintPrefix} <strong>{m.share.actions.nativeShareSave}</strong> / <strong>{m.share.actions.nativeShareGooglePhotos}</strong> / <strong>{m.share.actions.nativeShareInstagram}</strong> {m.share.actions.nativeShareHintSuffix}
                      </p>
                    )}
                    {supportsNativeShare && (
                      <button
                        type="button"
                        className="quiz-btn quiz-btn--outline quiz-btn--outline-ice"
                        onClick={() => handleShareImage('single-result-card', `trai-tim-bang-gia-${shareCardSpec.filenameSuffix}`, 'single', true)}
                        disabled={isGeneratingImage || !shareDisplayName.trim()}
                      >
                        {m.share.actions.download}
                      </button>
                    )}
                    <button
                      type="button"
                      className="quiz-btn quiz-btn--outline quiz-btn--outline-ice"
                      onClick={() => copyShareCaption('single')}
                    >
                      {m.share.actions.copyCaption}
                    </button>
                    {isGuestOnQuiz && (
                      <button
                        type="button"
                        className="quiz-btn quiz-btn--primary-gold"
                        onClick={() => { setActiveModal(null); goToCreateProfile(); }}
                      >
                        {m.quiz.joinDating}
                      </button>
                    )}
                    <button
                      type="button"
                      className="quiz-btn quiz-btn--outline"
                      onClick={() => setActiveModal(null)}
                    >
                      Đóng
                    </button>
                  </div>
                </div>
              )}

            </div>
          </div>,
          document.body
        )}

        {/* 
          Hidden offscreen QuizShareCard instances (story aspect).
          Portaled to body and positioned far offscreen so they never affect main layout or cause visual issues (e.g. black/blank screen on homepage when previous results exist in localStorage).
          They exist purely so auto-capture can find a rendered source for html2canvas when generating the default story card.
        */}
        {formData.extraDetails?.personalityResult && createPortal(
          <div style={{ position: 'fixed', left: -99999, top: -99999, width: 1, height: 1, overflow: 'hidden', opacity: 0, pointerEvents: 'none' }} aria-hidden="true">
            <QuizShareCard
              id="auto-personality-card"
              variant="personality"
              aspect="story"
              shareTitle={formData.extraDetails.personalityResult.shareTitle}
              shareHook={formData.extraDetails.personalityResult.shareHook}
              percent={formData.extraDetails.personalityResult.percent}
              typeKey={formData.extraDetails.personalityResult.typeKey}
              variantKey={formData.extraDetails.personalityResult.variantKey}
              displayName={autoDisplayName}
              city={shareProfileMeta.city}
              zodiac={shareProfileMeta.zodiac}
              zodiacSymbol={shareProfileMeta.zodiacSymbol}
              stats={getShareCardStats('personality', formData.extraDetails.personalityResult.typeKey, lang)}
              avatarUrl={autoAvatarUrl}
            />
          </div>,
          document.body
        )}

        {formData.extraDetails?.singleResult && createPortal(
          <div style={{ position: 'fixed', left: -99999, top: -99999, width: 1, height: 1, overflow: 'hidden', opacity: 0, pointerEvents: 'none' }} aria-hidden="true">
            <QuizShareCard
              id="auto-single-card"
              variant="single"
              aspect="story"
              shareTitle={formData.extraDetails.singleResult.shareTitle}
              shareHook={formData.extraDetails.singleResult.shareHook}
              percent={formData.extraDetails.singleResult.percent}
              typeKey={formData.extraDetails.singleResult.typeKey}
              variantKey={formData.extraDetails.singleResult.variantKey}
              temperature={formData.extraDetails.singleResult.temperature}
              temperatureLabel={formData.extraDetails.singleResult.temperatureLabel}
              triggerTagD={formData.extraDetails.singleResult.triggerTagD}
              triggerTagDBadge={FROZEN_HEART_TAG_D.cardBadge}
              displayName={autoDisplayName}
              city={shareProfileMeta.city}
              zodiac={shareProfileMeta.zodiac}
              zodiacSymbol={shareProfileMeta.zodiacSymbol}
              stats={getShareCardStats('single', formData.extraDetails.singleResult.typeKey, lang)}
              avatarUrl={autoAvatarUrl}
            />
          </div>,
          document.body
        )}
      </div>
    </div>
  );
}


